import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AlertTriangle, ChevronDown, ChevronUp, Globe, Loader2, Plus, Sparkles, Trash2, Upload, X } from 'lucide-react';
import SelectField from './SelectField';
import { guessGenderFromName } from '../utils/guessGender';
import { detectMailProvider, emailDomain } from '../utils/mailProviders';
import { isCompanyAsContactName } from '../utils/personName';
import { parseCategoryNames, parseContactGender } from '../utils/export';

const CATEGORY_COLOR_PRESETS = [
  '#0F766E',
  '#1D4ED8',
  '#7C3AED',
  '#BE185D',
  '#C2410C',
  '#A16207',
  '#475569',
  '#15803D',
];

/** Freemail-/Consumer-Domains, aus denen keine Firmenwebsite ableitbar ist */
const FREE_EMAIL_DOMAINS = new Set([
  'yahoo.com', 'yahoo.de', 'aol.com', 'proton.me', 'protonmail.com',
  't-online.de', 'freenet.de', 'online.de', 'arcor.de', 'mail.de',
  'posteo.de', 'mailbox.org', 'gmx.com', 'hotmail.de', 'live.de',
  'outlook.de', 'web.de', 'gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch',
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com',
  'msn.com', 'icloud.com', 'me.com', 'mac.com',
]);

const normalizeUrl = (value) => {
  if (!value) return '';
  const t = String(value).trim();
  if (t.toLowerCase().startsWith('http://')) return `https://${t.slice(7)}`;
  if (!/^https?:\/\//i.test(t)) return `https://${t}`;
  return t;
};

const emptyContact = () => ({
  name: '',
  email: '',
  company: '',
  phone: '',
  website: '',
  notes: '',
  gender: 'unknown',
  name2: '',
  gender2: 'unknown',
  categoryIds: [],
});

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const websiteDedupeKey = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const u = new URL(normalizeUrl(raw));
    const host = u.hostname.replace(/^www\./i, '').toLowerCase();
    const path = u.pathname.replace(/\/$/, '') || '';
    return `${host}${path}`;
  } catch (_) {
    return raw
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .toLowerCase()
      .replace(/\/$/, '');
  }
};

/** Grober Website-Vergleich nur über Host (ohne Pfad) — „kennen wir die Seite schon?“ */
const websiteHostKey = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    return new URL(normalizeUrl(raw)).hostname.replace(/^www\./i, '').toLowerCase();
  } catch (_) {
    return raw
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .split('/')[0]
      .toLowerCase()
      .replace(/:\d+$/, '');
  }
};

const normalizePersonNameKey = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9äöüß\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Firmenname für Duplikat-Vergleich (Rechtsform/Zeichensalat ignorieren). */
const normalizeCompanyKey = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[®©™]/g, ' ')
    .replace(
      /\b(?:gmbh|mbh|ag|ug|kg|ohg|gbr|co|ltd|inc|llc|plc|se|ek|e\.?\s*k\.?|haftungsbeschr(?:ä|ae)nkt)\b/gi,
      ' '
    )
    .replace(/[^a-z0-9äöüß\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Zu generische Firmen-Keys nicht als Duplikat werten („technik“, „bau“…). */
const isStrongCompanyKey = (key) => {
  const k = String(key || '').trim();
  if (!k || k.length < 4) return false;
  const tokens = k.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) return tokens.join(' ').length >= 6;
  return k.length >= 8;
};

const contactHasIndustry = (contact) => {
  if (String(contact?.industry || '').trim()) return true;
  return /Branche\s*:\s*\S+/i.test(String(contact?.notes || ''));
};

/** Branche aus Feld oder Notizen „Branche: …“ */
const extractIndustryLabel = (contactOrNotes) => {
  if (contactOrNotes && typeof contactOrNotes === 'object') {
    const direct = String(contactOrNotes.industry || '').trim();
    if (direct) return direct;
    return extractIndustryLabel(contactOrNotes.notes);
  }
  const notes = String(contactOrNotes || '');
  const m = notes.match(/Branche\s*:\s*([^\n]+)/i);
  return m ? String(m[1] || '').trim() : '';
};

const contactCompletenessScore = (contact) => {
  let score = 0;
  if (normalizeEmail(contact?.email)) score += 10;
  if (contactHasIndustry(contact)) score += 5;
  if (String(contact?.company || '').trim()) score += 2;
  if (String(contact?.name || '').trim()) score += 1;
  if (String(contact?.phone || '').trim()) score += 1;
  return score;
};

/** Typische Dauer einer KI-/Impressum-Suche (Fallback, bis echte Messwerte da sind). */
const AI_SEARCH_DEFAULT_MS = 55_000;
const SCRAPE_DEFAULT_MS = 45_000;

const formatDurationDe = (ms) => {
  const s = Math.max(0, Math.round(Number(ms) / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m} Min ${rem} s` : `${m} Min`;
  const h = Math.floor(m / 60);
  const mRem = m % 60;
  return mRem > 0 ? `${h} Std ${mRem} Min` : `${h} Std`;
};

const formatEtaDe = (ms) => {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  if (ms < 2500) return 'gleich fertig';
  return `noch ca. ${formatDurationDe(ms)}`;
};

const averageMs = (samples, fallback = AI_SEARCH_DEFAULT_MS) => {
  const list = (samples || []).filter((n) => Number.isFinite(n) && n > 0);
  if (!list.length) return fallback;
  return Math.round(list.reduce((a, b) => a + b, 0) / list.length);
};

/** Dieselbe KI-Auswahl wie auf der KI-Chat-Seite (localStorage). */
const AI_CHAT_BACKEND_KEY = 'odium_ai_backend_v1';
const AI_CHAT_MODEL_KEY = 'odium_ai_model_v1';

const getPreferredAiSelection = () => {
  let backend = 'auto';
  let model = '';
  try {
    backend = String(localStorage.getItem(AI_CHAT_BACKEND_KEY) || 'auto').trim().toLowerCase() || 'auto';
  } catch (_) {
    /* ignore */
  }
  try {
    model = String(localStorage.getItem(AI_CHAT_MODEL_KEY) || '').trim();
  } catch (_) {
    /* ignore */
  }
  return { backend, model };
};

const preferredAiInfoFromSelection = (sel) => {
  const backend = String(sel?.backend || 'auto').trim().toLowerCase() || 'auto';
  const model = String(sel?.model || '').trim();
  const label =
    backend === 'heim'
      ? 'Heim-KI'
      : backend === 'pi'
        ? 'Pi-KI'
        : backend === 'server'
          ? 'Server-KI'
          : 'Auto';
  return { id: backend, label, model };
};

/** Hinweistext für abgewählte Duplikate / bereits importierte Kontakte */
const formatDroppedDupes = (droppedExisting, droppedInFile, { websiteMode = false } = {}) => {
  const parts = [];
  if (droppedExisting > 0) {
    parts.push(
      websiteMode
        ? droppedExisting === 1
          ? '1 bereits bekannte Website'
          : `${droppedExisting} bereits bekannte Websites`
        : droppedExisting === 1
          ? '1 bereits vorhandener Kontakt'
          : `${droppedExisting} bereits vorhandene Kontakte`
    );
  }
  if (droppedInFile > 0) {
    parts.push(
      droppedInFile === 1
        ? '1 Duplikat in der Liste'
        : `${droppedInFile} Duplikate in der Liste`
    );
  }
  if (!parts.length) return '';
  return websiteMode
    ? `${parts.join(' · ')} entfernt (weniger KI-Arbeit)`
    : `${parts.join(' · ')} abgewählt`;
};

/** Mindestangaben: E-Mail + Kategorie, oder im Schlank-Modus E-Mail + Website */
const rowHasImportBasics = (
  contact,
  { requireWebsite = false, requireIndustry = true } = {}
) => {
  if (!normalizeEmail(contact?.email)) return false;
  if (requireWebsite && !String(contact?.website || '').trim()) return false;
  if (requireIndustry && !contactHasIndustry(contact)) return false;
  return true;
};

/**
 * Automatisch wählbar (ohne manuellen Override).
 * Duplikate / bereits importiert: Checkbox bleibt klickbar, Sync wählt ab.
 */
const rowCanBeSelected = (
  contact,
  {
    alreadyImported = false,
    duplicate = false,
    forceInclude = false,
    requireWebsite = false,
    requireIndustry = true,
  } = {}
) => {
  if (!rowHasImportBasics(contact, { requireWebsite, requireIndustry })) return false;
  if (forceInclude) return true;
  if (alreadyImported || duplicate) return false;
  return true;
};

/** Zwei Zeilen kollidieren (gleiche E-Mail / starke Firma / Name) */
const rowsConflict = (a, b) => {
  if (!a || !b || a.id === b.id) return false;
  const emailA = normalizeEmail(a.contact?.email);
  const emailB = normalizeEmail(b.contact?.email);
  if (emailA && emailB && emailA === emailB) return true;
  const companyA = normalizeCompanyKey(a.contact?.company);
  const companyB = normalizeCompanyKey(b.contact?.company);
  if (isStrongCompanyKey(companyA) && companyA === companyB) return true;
  const nameA = normalizePersonNameKey(a.contact?.name);
  const nameB = normalizePersonNameKey(b.contact?.name);
  if (nameA.length >= 3 && nameA === nameB) return true;
  return false;
};

const normalizeCategoryIds = (value) =>
  (Array.isArray(value) ? value : [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);

const resolveCategoryIdsFromNames = (names, categoriesList) => {
  if (!Array.isArray(names) || !names.length || !Array.isArray(categoriesList)) return [];
  const byName = new Map(
    categoriesList.map((cat) => [String(cat.name || '').trim().toLowerCase(), Number(cat.id)])
  );
  const ids = [];
  for (const name of names) {
    const id = byName.get(String(name || '').trim().toLowerCase());
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
};

/**
 * Vorschau + Korrektur vor dem Kontaktimport (CSV/Excel, inkl. Kategorien).
 */
const ContactCsvImport = ({
  open,
  rows: initialRows = null,
  fileName = '',
  mode = 'full',
  onClose,
  existingContacts = [],
  existingCategories = null,
  onImported,
}) => {
  const isWebsiteMode = mode === 'website';
  const isEmailWebsiteMode = mode === 'emailWebsite';
  // E-Mail + Website: Auswahl nicht wegen fehlender Kategorie abwählen
  const basicsOpts = isEmailWebsiteMode
    ? { requireWebsite: false, requireIndustry: false }
    : { requireWebsite: false, requireIndustry: true };
  const [rows, setRows] = useState([]);
  const [droppedDupesInfo, setDroppedDupesInfo] = useState('');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [bulkCategoryIds, setBulkCategoryIds] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState(CATEGORY_COLOR_PRESETS[0]);
  const [scrapingRowId, setScrapingRowId] = useState(null);
  const [aiSearchingRowId, setAiSearchingRowId] = useState(null);
  const [aiSearchLive, setAiSearchLive] = useState(null);
  // { rowId, current, steps: [{ts, message, step}], sources, query, startedAt }
  const [aiSearchTick, setAiSearchTick] = useState(0);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkGenerateProgress, setBulkGenerateProgress] = useState(null);
  const [bulkAiSearching, setBulkAiSearching] = useState(false);
  const [bulkAiProgress, setBulkAiProgress] = useState(null);
  const bulkAbortRef = useRef(false);
  const bulkAbortControllerRef = useRef(null);
  const aiAbortRef = useRef(null);
  const aiReaderRef = useRef(null);
  const aiCancelRequestedRef = useRef(false);
  const aiLogRef = useRef(null);
  const aiLogStickToBottomRef = useRef(true);
  /** Letzte gemessene KI-Suche-Dauern (ms) für ETA */
  const aiDurationSamplesRef = useRef([]);
  /** Letzte gemessene Impressum-/Generate-Dauern (ms) für ETA */
  const scrapeDurationSamplesRef = useRef([]);
  /** Aktuell genutzte KI (Backend + Modell) */
  const [activeAiInfo, setActiveAiInfo] = useState(null);
  const importListRef = useRef(null);
  const autoWebsiteScrapeRef = useRef(false);
  const rowsRef = useRef([]);
  const categoriesRef = useRef([]);
  const categoryCreateInflightRef = useRef(new Map());

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  const scrollImportListToTop = () => {
    const el = importListRef.current;
    if (el) el.scrollTop = 0;
  };

  const scrollImportRowIntoView = (rowId) => {
    if (rowId == null) return;
    const root = importListRef.current;
    if (!root) return;
    const node = root.querySelector(`[data-import-row-id="${rowId}"]`);
    if (!node || typeof node.scrollIntoView !== 'function') return;
    try {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch (_) {
      node.scrollIntoView(true);
    }
  };

  const pushAiDurationSample = (ms) => {
    const n = Number(ms);
    if (!Number.isFinite(n) || n < 1500) return;
    const next = [...aiDurationSamplesRef.current, n].slice(-20);
    aiDurationSamplesRef.current = next;
  };

  const pushScrapeDurationSample = (ms) => {
    const n = Number(ms);
    if (!Number.isFinite(n) || n < 1500) return;
    const next = [...scrapeDurationSamplesRef.current, n].slice(-20);
    scrapeDurationSamplesRef.current = next;
  };

  const estimateAiSearchAvgMs = () => averageMs(aiDurationSamplesRef.current, AI_SEARCH_DEFAULT_MS);
  const estimateScrapeAvgMs = () => averageMs(scrapeDurationSamplesRef.current, SCRAPE_DEFAULT_MS);

  const applyAiInfoFromEvent = (data) => {
    const label = String(data?.backendLabel || data?.label || '').trim();
    const model = String(data?.model || '').trim();
    if (!label && !model) return;
    setActiveAiInfo((prev) => ({
      label: label || prev?.label || '',
      model: model || prev?.model || '',
      id: data?.backend || data?.id || prev?.id || '',
    }));
  };

  const formatAiInfoLabel = (info) => {
    if (!info) return '';
    const label = String(info.label || '').trim();
    const model = String(info.model || '').trim();
    if (label && model) return `${label} · ${model}`;
    return label || model;
  };

  useEffect(() => {
    if (!open) {
      bulkAbortRef.current = true;
      try {
        bulkAbortControllerRef.current?.abort();
      } catch (_) {
        /* ignore */
      }
      bulkAbortControllerRef.current = null;
      setRows([]);
      setImporting(false);
      setImportProgress(null);
      setBulkCategoryIds([]);
      setShowNewCategory(false);
      setNewCategoryName('');
      setScrapingRowId(null);
      setAiSearchingRowId(null);
      setAiSearchLive(null);
      aiCancelRequestedRef.current = true;
      try {
        aiAbortRef.current?.abort();
      } catch (_) {
        /* ignore */
      }
      aiAbortRef.current = null;
      try {
        aiReaderRef.current?.cancel?.();
      } catch (_) {
        /* ignore */
      }
      aiReaderRef.current = null;
      setBulkGenerating(false);
      setBulkGenerateProgress(null);
      setBulkAiSearching(false);
      setBulkAiProgress(null);
      setDroppedDupesInfo('');
      setActiveAiInfo(null);
      autoWebsiteScrapeRef.current = false;
      return;
    }
    bulkAbortRef.current = false;
    aiCancelRequestedRef.current = false;
    setDroppedDupesInfo('');
    autoWebsiteScrapeRef.current = false;
    setCategoriesExpanded(true);
    const seedCategories = Array.isArray(existingCategories) ? existingCategories : [];
    if (seedCategories.length) {
      setCategories(seedCategories);
    }

    let cancelled = false;
    const list = Array.isArray(initialRows) ? initialRows : [];

    const buildRows = (categoriesList) => {
      const known = [...existingContacts];
      const existingEmails = new Set(
        existingContacts.map((c) => normalizeEmail(c.email)).filter(Boolean)
      );
      const existingNames = new Set(
        existingContacts.map((c) => normalizePersonNameKey(c.name)).filter((n) => n.length >= 3)
      );
      const existingCompanies = new Set(
        existingContacts
          .map((c) => normalizeCompanyKey(c.company))
          .filter((k) => isStrongCompanyKey(k))
      );
      const existingWebsites = new Set();
      existingContacts.forEach((c) => {
        const host = websiteHostKey(c.website);
        if (host) existingWebsites.add(host);
        const email = normalizeEmail(c.email);
        if (!email || !email.includes('@')) return;
        if (detectMailProvider(email) || FREE_EMAIL_DOMAINS.has(emailDomain(email))) return;
        const domain = emailDomain(email);
        if (domain) existingWebsites.add(domain.replace(/^www\./i, '').toLowerCase());
      });
      const seenInFile = new Set();
      const seenNamesInFile = new Set();
      const seenCompaniesInFile = new Set();
      const seenWebsitesInFile = new Set();
      const kept = [];
      let droppedExisting = 0;
      let droppedInFile = 0;

      list.forEach((raw, index) => {
        if (isWebsiteMode) {
          const websiteRaw = String(raw.website || '').trim();
          if (!websiteRaw) return;
          const website = normalizeUrl(websiteRaw);
          const hostKey = websiteHostKey(website);
          if (!hostKey) return;
          if (existingWebsites.has(hostKey)) {
            droppedExisting += 1;
            return;
          }
          if (seenWebsitesInFile.has(hostKey)) {
            droppedInFile += 1;
            return;
          }
          seenWebsitesInFile.add(hostKey);
          kept.push({
            id: `csv-${index}`,
            index,
            selected: false,
            duplicate: false,
            contact: {
              ...emptyContact(),
              website,
            },
            warnings: ['Daten werden aus dem Impressum geladen…'],
          });
          return;
        }

        if (isEmailWebsiteMode) {
          const email = normalizeEmail(raw.email);
          const websiteRaw = String(raw.website || '').trim();
          const website = websiteRaw ? normalizeUrl(websiteRaw) : '';
          if (!email && !website) return;

          const hostKey = websiteHostKey(website);
          // Bereits vorhanden: primär per E-Mail, sonst (ohne E-Mail) per Website
          const alreadyImported = Boolean(
            (email && existingEmails.has(email)) ||
              (!email && hostKey && existingWebsites.has(hostKey))
          );
          // In-Datei-Duplikate behalten — nur zählen, nicht entfernen/abwählen
          const duplicateInFile = Boolean(
            (email && seenInFile.has(email)) ||
              (!email && hostKey && seenWebsitesInFile.has(hostKey))
          );
          if (email) seenInFile.add(email);
          if (hostKey) seenWebsitesInFile.add(hostKey);
          if (alreadyImported) droppedExisting += 1;
          else if (duplicateInFile) droppedInFile += 1;

          const categoryNames = Array.isArray(raw.categoryNames)
            ? raw.categoryNames.map((n) => String(n || '').trim()).filter(Boolean)
            : parseCategoryNames(raw.categories || raw.category || '');
          const resolvedIds = resolveCategoryIdsFromNames(categoryNames, categoriesList);
          const contact = {
            ...emptyContact(),
            email,
            website,
            categoryNames,
            categoryIds: [
              ...normalizeCategoryIds(raw.categoryIds),
              ...resolvedIds,
            ].filter((id, i, arr) => arr.indexOf(id) === i),
          };

          const warnings = [];
          if (!email) warnings.push('E-Mail fehlt');
          if (!website) warnings.push('Website fehlt');
          if (!contactHasIndustry(contact)) warnings.push('Kategorie fehlt');
          if (alreadyImported) warnings.push('Bereits importiert');
          if (duplicateInFile) warnings.push('Duplikat in der Liste');
          if (categoriesList.length && categoryNames.length) {
            const unresolved = categoryNames.filter(
              (name) =>
                !categoriesList.some(
                  (cat) =>
                    String(cat.name || '').trim().toLowerCase() === String(name).trim().toLowerCase()
                )
            );
            if (unresolved.length) {
              warnings.push(`Unbekannte Kategorie(n): ${unresolved.join(', ')}`);
            }
          }

          kept.push({
            id: `csv-${index}`,
            index,
            // Nur bereits vorhandene Kontakte abwählen — alles andere bleibt an
            selected: !alreadyImported,
            alreadyImported,
            duplicate: duplicateInFile,
            contact,
            warnings,
          });
          return;
        }

        const name2 = String(raw.name2 || '').trim();
        const importedGender = raw.gender ? parseContactGender(raw.gender) : '';
        const importedGender2 = raw.gender2 ? parseContactGender(raw.gender2) : '';
        const categoryNames = Array.isArray(raw.categoryNames)
          ? raw.categoryNames.map((n) => String(n || '').trim()).filter(Boolean)
          : parseCategoryNames(raw.categories || raw.category || '');
        const resolvedIds = resolveCategoryIdsFromNames(categoryNames, categoriesList);
        const contact = {
          ...emptyContact(),
          name: String(raw.name || '').trim(),
          email: normalizeEmail(raw.email),
          company: String(raw.company || '').trim(),
          phone: String(raw.phone || '').trim(),
          website: String(raw.website || '').trim(),
          notes: String(raw.notes || '').trim(),
          name2,
          categoryNames,
          categoryIds: [
            ...normalizeCategoryIds(raw.categoryIds),
            ...resolvedIds,
          ].filter((id, i, arr) => arr.indexOf(id) === i),
        };
        contact.gender =
          importedGender && importedGender !== 'unknown'
            ? importedGender
            : guessGenderFromName(contact.name, known);

        if (contact.name) known.push({ name: contact.name, gender: contact.gender });

        if (contact.name2) {
          contact.gender2 =
            importedGender2 && importedGender2 !== 'unknown'
              ? importedGender2
              : guessGenderFromName(contact.name2, known);
          known.push({ name: contact.name2, gender: contact.gender2 });
        } else {
          contact.gender2 = 'unknown';
        }

        const email = contact.email;
        const nameKey = normalizePersonNameKey(contact.name);
        const companyKey = normalizeCompanyKey(contact.company);
        if (email && existingEmails.has(email)) {
          droppedExisting += 1;
          return;
        }
        if (isStrongCompanyKey(companyKey) && existingCompanies.has(companyKey)) {
          droppedExisting += 1;
          return;
        }
        if (nameKey.length >= 3 && existingNames.has(nameKey)) {
          droppedExisting += 1;
          return;
        }
        if (email && seenInFile.has(email)) {
          droppedInFile += 1;
          return;
        }
        if (isStrongCompanyKey(companyKey) && seenCompaniesInFile.has(companyKey)) {
          droppedInFile += 1;
          return;
        }
        if (nameKey.length >= 3 && seenNamesInFile.has(nameKey)) {
          droppedInFile += 1;
          return;
        }
        if (email) seenInFile.add(email);
        if (isStrongCompanyKey(companyKey)) seenCompaniesInFile.add(companyKey);
        if (nameKey.length >= 3) seenNamesInFile.add(nameKey);

        const warnings = [];
        if (!email) warnings.push('E-Mail fehlt');
        const hasIndustry = contactHasIndustry(contact);
        if (!hasIndustry) warnings.push('Kategorie fehlt');
        if (categoriesList.length) {
          const unresolved = categoryNames.filter(
            (name) =>
              !categoriesList.some(
                (cat) =>
                  String(cat.name || '').trim().toLowerCase() === String(name).trim().toLowerCase()
              )
          );
          if (unresolved.length) {
            warnings.push(`Unbekannte Kategorie(n): ${unresolved.join(', ')}`);
          }
        }

        kept.push({
          id: `csv-${index}`,
          index,
          selected: Boolean(email && hasIndustry),
          duplicate: false,
          contact,
          warnings,
        });
      });

      return { rows: kept, droppedExisting, droppedInFile };
    };

    const applyBuilt = (categoriesList, { notify = false } = {}) => {
      const built = buildRows(categoriesList);
      setRows(built.rows);
      const info = formatDroppedDupes(
        built.droppedExisting,
        // Datei-Duplikate bleiben ausgewählt — nicht als „abgewählt“ melden
        isEmailWebsiteMode ? 0 : built.droppedInFile,
        {
          websiteMode: isWebsiteMode,
        }
      );
      setDroppedDupesInfo(info);
      if (notify && info) {
        toast.success(info);
      }
    };

    applyBuilt(seedCategories, { notify: isWebsiteMode || isEmailWebsiteMode });

    axios
      .get('/api/contact-categories')
      .then((res) => {
        if (cancelled) return;
        const nextCategories = Array.isArray(res.data?.categories) ? res.data.categories : [];
        setCategories(nextCategories);
        // Website-Modus: Zeilen nicht neu aufbauen — Auto-Scrape läuft sonst ins Leere
        if (isWebsiteMode) return;
        applyBuilt(nextCategories, { notify: true });
      })
      .catch(() => {
        if (!cancelled) {
          if (!seedCategories.length) setCategories([]);
          if (!isWebsiteMode) applyBuilt(seedCategories, { notify: true });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, initialRows, existingContacts, existingCategories, isWebsiteMode, isEmailWebsiteMode]);

  useEffect(() => {
    if (scrapingRowId == null && aiSearchingRowId == null) return;
    scrollImportRowIntoView(scrapingRowId || aiSearchingRowId);
  }, [scrapingRowId, aiSearchingRowId]);

  useEffect(() => {
    if (!aiSearchingRowId && !bulkAiSearching && !scrapingRowId && !bulkGenerating) return undefined;
    const id = window.setInterval(() => setAiSearchTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [aiSearchingRowId, bulkAiSearching, scrapingRowId, bulkGenerating]);

  const bulkAiEtaLabel = useMemo(() => {
    if (!bulkAiSearching || !bulkAiProgress?.total) return null;
    void aiSearchTick;
    const { done = 0, total = 0, currentStartedAt } = bulkAiProgress;
    const remainingUnits = Math.max(0, total - done);
    if (remainingUnits <= 0) return 'gleich fertig';
    const avg = Number(bulkAiProgress.avgMs) || estimateAiSearchAvgMs();
    const currentElapsed = currentStartedAt ? Date.now() - currentStartedAt : 0;
    const etaMs = Math.max(0, remainingUnits * avg - currentElapsed);
    return formatEtaDe(etaMs);
  }, [bulkAiSearching, bulkAiProgress, aiSearchTick]);

  const bulkGenerateEtaLabel = useMemo(() => {
    if (!bulkGenerating || !bulkGenerateProgress?.total) return null;
    void aiSearchTick;
    const { done = 0, total = 0, currentStartedAt } = bulkGenerateProgress;
    const remainingUnits = Math.max(0, total - done);
    if (remainingUnits <= 0) return 'gleich fertig';
    const avg = Number(bulkGenerateProgress.avgMs) || estimateScrapeAvgMs();
    const currentElapsed = currentStartedAt ? Date.now() - currentStartedAt : 0;
    const etaMs = Math.max(0, remainingUnits * avg - currentElapsed);
    return formatEtaDe(etaMs);
  }, [bulkGenerating, bulkGenerateProgress, aiSearchTick]);

  const singleAiEtaLabel = useMemo(() => {
    if (!aiSearchingRowId || !aiSearchLive?.startedAt || aiSearchLive.rowId !== aiSearchingRowId) {
      return null;
    }
    void aiSearchTick;
    const elapsed = Date.now() - aiSearchLive.startedAt;
    const avg = estimateAiSearchAvgMs();
    return formatEtaDe(Math.max(0, avg - elapsed));
  }, [aiSearchingRowId, aiSearchLive?.startedAt, aiSearchLive?.rowId, aiSearchTick]);

  const singleScrapeEtaLabel = useMemo(() => {
    if (!scrapingRowId || !aiSearchLive?.startedAt || aiSearchLive.rowId !== scrapingRowId) {
      return null;
    }
    if (aiSearchLive.mode && aiSearchLive.mode !== 'scrape') return null;
    void aiSearchTick;
    const elapsed = Date.now() - aiSearchLive.startedAt;
    const avg = estimateScrapeAvgMs();
    return formatEtaDe(Math.max(0, avg - elapsed));
  }, [scrapingRowId, aiSearchLive?.startedAt, aiSearchLive?.rowId, aiSearchLive?.mode, aiSearchTick]);

  useEffect(() => {
    if (!aiSearchLive?.steps?.length) return;
    if (!aiLogStickToBottomRef.current) return;
    const el = aiLogRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [aiSearchLive?.steps, aiSearchLive?.rowId]);

  const selectedCount = useMemo(
    () => rows.filter((r) => r.selected).length,
    [rows]
  );

  /** Auswahl / Duplikat-Flags live für alle Zeilen neu berechnen */
  const syncRowsSelectionState = (list) => {
    const existingEmails = new Set(
      existingContacts.map((c) => normalizeEmail(c.email)).filter(Boolean)
    );
    const existingNames = new Set(
      existingContacts.map((c) => normalizePersonNameKey(c.name)).filter((n) => n.length >= 3)
    );
    const existingCompanies = new Set(
      existingContacts
        .map((c) => normalizeCompanyKey(c.company))
        .filter((k) => isStrongCompanyKey(k))
    );
    const existingWebsites = new Set();
    existingContacts.forEach((c) => {
      const host = websiteHostKey(c.website);
      if (host) existingWebsites.add(host);
      const email = normalizeEmail(c.email);
      if (!email || !email.includes('@')) return;
      if (detectMailProvider(email) || FREE_EMAIL_DOMAINS.has(emailDomain(email))) return;
      const domain = emailDomain(email);
      if (domain) existingWebsites.add(domain.replace(/^www\./i, '').toLowerCase());
    });

    // E-Mail + Website: nur bereits vorhandene Kontakte abwählen — sonst alles anlassen
    if (isEmailWebsiteMode) {
      let alreadyImportedCount = 0;
      let duplicateCount = 0;
      const seenEmailsInList = new Set();
      const seenHostsInList = new Set();

      const synced = list.map((row) => {
        const email = normalizeEmail(row.contact.email);
        const hostKey = websiteHostKey(row.contact.website);
        const alreadyImported = Boolean(
          (email && existingEmails.has(email)) ||
            (!email && hostKey && existingWebsites.has(hostKey))
        );
        if (alreadyImported) alreadyImportedCount += 1;

        const duplicate = Boolean(
          !alreadyImported &&
            ((email && seenEmailsInList.has(email)) ||
              (!email && hostKey && seenHostsInList.has(hostKey)))
        );
        if (duplicate) duplicateCount += 1;
        if (email) seenEmailsInList.add(email);
        if (hostKey) seenHostsInList.add(hostKey);

        let warnings = (row.warnings || []).filter(
          (w) =>
            !/E-Mail bereits|Doppelte E-Mail|bereits vorhanden|bereits importiert|Doppelte Firma|Unternehmen bereits|Duplikat in der Liste|Daten werden aus (der Website|dem Impressum)/i.test(
              String(w || '')
            ) &&
            !/^E-Mail fehlt$/i.test(String(w || '')) &&
            !/^Website fehlt$/i.test(String(w || '')) &&
            !/^Kategorie fehlt$/i.test(String(w || '')) &&
            !/^Branche fehlt$/i.test(String(w || ''))
        );
        if (!email) warnings.push('E-Mail fehlt');
        if (!String(row.contact.website || '').trim()) warnings.push('Website fehlt');
        if (!contactHasIndustry(row.contact)) warnings.push('Kategorie fehlt');
        if (alreadyImported) warnings.push('Bereits importiert');
        if (duplicate) warnings.push('Duplikat in der Liste');

        const forceInclude = Boolean(row.forceInclude);
        let selected;
        if (forceInclude) {
          selected = Boolean(row.selected);
        } else if (alreadyImported) {
          selected = false;
        } else {
          // Neu: immer an — auch ohne Kategorie / bei Datei-Duplikat
          selected = true;
        }

        const { previousEmail: _pe, previousHadIndustry: _pi, ...rest } = row;
        return {
          ...rest,
          alreadyImported,
          duplicate,
          forceInclude: alreadyImported ? false : forceInclude,
          warnings,
          selected,
        };
      });

      return { rows: synced, alreadyImportedCount, duplicateCount };
    }

    // 1) Bereits importiert markieren (Zeilen bleiben sichtbar)
    let alreadyImportedCount = 0;
    const withExistingFlags = list.map((row) => {
      const email = normalizeEmail(row.contact.email);
      const nameKey = normalizePersonNameKey(row.contact.name);
      const companyKey = normalizeCompanyKey(row.contact.company);
      const hostKey = websiteHostKey(row.contact.website);
      const alreadyImported = Boolean(
        (email && existingEmails.has(email)) ||
          (isStrongCompanyKey(companyKey) && existingCompanies.has(companyKey)) ||
          (nameKey.length >= 3 && existingNames.has(nameKey)) ||
          (hostKey && existingWebsites.has(hostKey))
      );
      if (alreadyImported) alreadyImportedCount += 1;
      return { ...row, alreadyImported, duplicate: false };
    });

    // 2) Doppelte in der Liste: vollständigere Zeile gewinnt, Verlierer nur abwählen
    const ordered = [...withExistingFlags]
      .filter((r) => !r.alreadyImported)
      .sort(
        (a, b) =>
          contactCompletenessScore(b.contact) - contactCompletenessScore(a.contact) ||
          (a.index ?? 0) - (b.index ?? 0)
      );
    const claimedEmails = new Set();
    const claimedCompanies = new Set();
    const claimedNames = new Set();
    const winnerIds = new Set();
    for (const row of ordered) {
      const email = normalizeEmail(row.contact.email);
      const companyKey = normalizeCompanyKey(row.contact.company);
      const nameKey = normalizePersonNameKey(row.contact.name);
      if (email && claimedEmails.has(email)) continue;
      if (isStrongCompanyKey(companyKey) && claimedCompanies.has(companyKey)) continue;
      if (nameKey.length >= 3 && claimedNames.has(nameKey)) continue;
      if (email) claimedEmails.add(email);
      if (isStrongCompanyKey(companyKey)) claimedCompanies.add(companyKey);
      if (nameKey.length >= 3) claimedNames.add(nameKey);
      winnerIds.add(row.id);
    }

    let duplicateCount = 0;
    const synced = withExistingFlags.map((row) => {
      const duplicate = !row.alreadyImported && !winnerIds.has(row.id);
      if (duplicate) duplicateCount += 1;

      const email = normalizeEmail(row.contact.email);
      let warnings = (row.warnings || []).filter(
        (w) =>
          !/E-Mail bereits|Doppelte E-Mail|bereits vorhanden|bereits importiert|Doppelte Firma|Unternehmen bereits|Duplikat in der Liste|Daten werden aus (der Website|dem Impressum)/i.test(
            String(w || '')
          ) &&
          !/^E-Mail fehlt$/i.test(String(w || '')) &&
          !/^Website fehlt$/i.test(String(w || '')) &&
          !/^Kategorie fehlt$/i.test(String(w || '')) &&
          !/^Branche fehlt$/i.test(String(w || ''))
      );
      if (!email) warnings.push('E-Mail fehlt');
      if (isEmailWebsiteMode && !String(row.contact.website || '').trim()) {
        warnings.push('Website fehlt');
      }
      const hasIndustry = contactHasIndustry(row.contact);
      if (!hasIndustry) warnings.push('Kategorie fehlt');
      if (row.alreadyImported) warnings.push('Bereits importiert');
      if (duplicate) warnings.push('Duplikat in der Liste');

      const previousEmail = row.previousEmail;
      const previousHadIndustry = row.previousHadIndustry;
      const { previousEmail: _pe, previousHadIndustry: _pi, ...rest } = row;
      const forceInclude = Boolean(row.forceInclude);
      const canAutoSelect = rowCanBeSelected(row.contact, {
        ...basicsOpts,
        alreadyImported: row.alreadyImported,
        duplicate,
        forceInclude: false,
      });
      const newlyComplete =
        canAutoSelect &&
        !forceInclude &&
        !row.alreadyImported &&
        !duplicate &&
        (!previousEmail || !previousHadIndustry);

      // Manuelle Auswahl (forceInclude) immer respektieren — Checkbox nie „fest“ abwählen
      let selected = false;
      if (forceInclude) {
        selected = Boolean(row.selected);
      } else if (canAutoSelect) {
        selected = Boolean(row.selected || newlyComplete);
      }

      return {
        ...rest,
        alreadyImported: row.alreadyImported,
        duplicate,
        forceInclude,
        warnings,
        selected,
      };
    });

    // 3) Im ausgewählten Pool: höchstens ein Kontakt je E-Mail/Firma/Name
    const selectedOrdered = synced
      .filter((r) => r.selected)
      .sort(
        (a, b) =>
          Number(b.forceInclude) - Number(a.forceInclude) ||
          contactCompletenessScore(b.contact) - contactCompletenessScore(a.contact) ||
          (a.index ?? 0) - (b.index ?? 0)
      );
    const keepSelectedIds = new Set();
    const poolEmails = new Set();
    const poolCompanies = new Set();
    const poolNames = new Set();
    for (const row of selectedOrdered) {
      const email = normalizeEmail(row.contact.email);
      const companyKey = normalizeCompanyKey(row.contact.company);
      const nameKey = normalizePersonNameKey(row.contact.name);
      if (email && poolEmails.has(email)) continue;
      if (isStrongCompanyKey(companyKey) && poolCompanies.has(companyKey)) continue;
      if (nameKey.length >= 3 && poolNames.has(nameKey)) continue;
      if (email) poolEmails.add(email);
      if (isStrongCompanyKey(companyKey)) poolCompanies.add(companyKey);
      if (nameKey.length >= 3) poolNames.add(nameKey);
      keepSelectedIds.add(row.id);
    }

    const dedupedSelected = synced.map((row) => {
      if (!row.selected || keepSelectedIds.has(row.id)) return row;
      return { ...row, selected: false, forceInclude: false };
    });

    return { rows: dedupedSelected, alreadyImportedCount, duplicateCount };
  };

  const updateRowContact = (id, patch) => {
    setRows((prev) => {
      const next = prev.map((row) => {
        if (row.id !== id) return row;
        const previousEmail = normalizeEmail(row.contact.email);
        const previousHadIndustry = contactHasIndustry(row.contact);
        const contact = { ...row.contact, ...patch };
        if (Object.prototype.hasOwnProperty.call(patch, 'email')) {
          contact.email = normalizeEmail(patch.email);
        }
        if (Object.prototype.hasOwnProperty.call(patch, 'categoryIds')) {
          contact.categoryIds = normalizeCategoryIds(patch.categoryIds);
        }
        return { ...row, contact, previousEmail, previousHadIndustry };
      });

      const { rows: synced } = syncRowsSelectionState(next);

      const newlyBlocked = synced.filter((r) => {
        if (!(r.alreadyImported || r.duplicate)) return false;
        const before = prev.find((p) => p.id === r.id);
        if (!before) return false;
        const wasBlocked = Boolean(before.alreadyImported || before.duplicate);
        return !wasBlocked || Boolean(before.selected);
      });

      if (newlyBlocked.length > 0) {
        const newlyExisting = newlyBlocked.filter((r) => r.alreadyImported).length;
        const newlyDupes = newlyBlocked.filter((r) => r.duplicate && !r.alreadyImported).length;
        const info = formatDroppedDupes(newlyExisting, newlyDupes);
        if (info) {
          queueMicrotask(() => toast.success(info));
          setDroppedDupesInfo(info);
        }
      }

      return synced;
    });
  };

  const applyContactDataToRow = async (rowId, prev, data, { preserveEmail = false } = {}) => {
    const d = data || {};
    // Live-Stand: Nutzer darf während KI/Import tippen — bestehende Werte nicht überschreiben
    const live = rowsRef.current.find((r) => r.id === rowId)?.contact || prev || {};
    const keep = (current, incoming) => {
      const cur = String(current || '').trim();
      if (cur) return cur;
      return String(incoming || '').trim();
    };
    const cleanPerson = (v) =>
      String(v || '')
        .replace(/\s+/g, ' ')
        .replace(/^[,;\s"'„“”|.\-–—]+|[,;\s"'„“”|.\-–—]+$/g, '')
        .trim();
    const nextName = cleanPerson(keep(live.name, d.name));
    const nextName2 = Object.prototype.hasOwnProperty.call(d, 'name2')
      ? cleanPerson(keep(live.name2, d.name2))
      : cleanPerson(live.name2 || '');
    const nextCompany = keep(live.company, d.company);
    const patch = {
      website: keep(live.website, d.website) || String(prev?.website || '').trim(),
      name: nextName,
      name2: nextName2 || '',
      email: preserveEmail
        ? live.email || prev?.email || ''
        : keep(live.email, d.email),
      company: nextCompany,
      phone: keep(live.phone, d.phone),
    };
    if (d.website && String(d.website).trim() && !String(live.website || '').trim()) {
      patch.website = normalizeUrl(d.website);
    }
    if (Object.prototype.hasOwnProperty.call(d, 'notes') && d.notes != null) {
      const incoming = String(d.notes).trim();
      const prevNotes = String(live.notes || prev?.notes || '').trim();
      if (!prevNotes) {
        patch.notes = incoming;
      } else if (!incoming) {
        patch.notes = prevNotes;
      } else if (/Branche\s*:/i.test(incoming)) {
        const ind = incoming
          .split(/\n/)
          .map((l) => l.trim())
          .find((l) => /^Branche\s*:/i.test(l))
          ?.replace(/^Branche\s*:\s*/i, '')
          .trim();
        if (ind) {
          patch.notes = /Branche\s*:/i.test(prevNotes)
            ? prevNotes.replace(/Branche\s*:[^\n]*/i, `Branche: ${ind}`).trim()
            : `${prevNotes}\nBranche: ${ind}`;
        } else {
          patch.notes = prevNotes;
        }
      } else {
        patch.notes = prevNotes;
      }
    }
    const industry = String(d.industry || '').trim();
    if (industry && !contactHasIndustry(live)) {
      patch.industry = industry;
      const baseNotes = String(patch.notes != null ? patch.notes : live.notes || prev?.notes || '').trim();
      patch.notes = /Branche\s*:/i.test(baseNotes)
        ? baseNotes.replace(/Branche\s*:[^\n]*/i, `Branche: ${industry}`).trim()
        : baseNotes
          ? `${baseNotes}\nBranche: ${industry}`
          : `Branche: ${industry}`;
    }
    if (d.name && String(d.name).trim()) {
      patch.gender = isCompanyAsContactName(nextName, nextCompany)
        ? 'unknown'
        : guessGenderFromName(nextName, existingContacts);
    }
    if (nextName2) {
      patch.gender2 = guessGenderFromName(nextName2, existingContacts);
    } else if (d.name2 != null) {
      patch.gender2 = 'unknown';
    }
    updateRowContact(rowId, patch);

    const industryLabel =
      industry ||
      extractIndustryLabel({
        industry: patch.industry,
        notes: patch.notes != null ? patch.notes : prev.notes,
      });
    if (industryLabel) {
      await assignIndustryCategoryToRow(rowId, industryLabel);
    }

    return ['name', 'name2', 'email', 'company', 'phone', 'website', 'notes', 'industry'].filter(
      (k) => d[k] && String(d[k]).trim()
    );
  };

  /** Kategorie je Branche anlegen (falls nötig) und der Zeile zuweisen. */
  const ensureIndustryCategory = async (industryRaw) => {
    const name = String(industryRaw || '').trim().slice(0, 80);
    if (!name) return null;
    const key = name.toLowerCase();
    const existing = categoriesRef.current.find(
      (c) => String(c.name || '').trim().toLowerCase() === key
    );
    if (existing) return existing;

    const inflight = categoryCreateInflightRef.current;
    if (inflight.has(key)) {
      return inflight.get(key);
    }

    const color =
      CATEGORY_COLOR_PRESETS[
        Math.abs([...key].reduce((acc, ch) => acc + ch.charCodeAt(0), 0)) %
          CATEGORY_COLOR_PRESETS.length
      ];

    const promise = (async () => {
      try {
        const res = await axios.post('/api/contact-categories', { name, color });
        const created = res.data?.category;
        if (created?.id) {
          setCategories((prev) => {
            if (prev.some((c) => Number(c.id) === Number(created.id))) return prev;
            const next = [...prev, created].sort((a, b) =>
              String(a.name || '').localeCompare(String(b.name || ''), 'de', {
                sensitivity: 'base',
              })
            );
            categoriesRef.current = next;
            return next;
          });
          return created;
        }
      } catch (err) {
        if (err?.response?.status === 409) {
          try {
            const res = await axios.get('/api/contact-categories');
            const list = Array.isArray(res.data?.categories) ? res.data.categories : [];
            setCategories(list);
            categoriesRef.current = list;
            return (
              list.find((c) => String(c.name || '').trim().toLowerCase() === key) || null
            );
          } catch (_) {
            /* ignore */
          }
        }
      }
      return null;
    })().finally(() => {
      inflight.delete(key);
    });

    inflight.set(key, promise);
    return promise;
  };

  const assignIndustryCategoryToRow = async (rowId, industryRaw) => {
    const cat = await ensureIndustryCategory(industryRaw);
    if (!cat?.id) return null;
    const catId = Number(cat.id);
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const current = normalizeCategoryIds(row.contact.categoryIds);
        if (current.includes(catId)) return row;
        return {
          ...row,
          contact: {
            ...row.contact,
            categoryIds: [...current, catId],
          },
        };
      })
    );
    return cat;
  };

  /** Zeilen ohne E-Mail / Duplikate / bereits Importierte abwählen. */
  const uncheckRowsWithoutEmail = () => {
    setRows((prev) => syncRowsSelectionState(prev).rows);
  };

  const scrapeImpressumIntoRow = async (
    rowId,
    websiteRaw,
    { preserveEmail = false, silent = false, contactOverride = null, signal = null } = {}
  ) => {
    if (!websiteRaw?.trim()) {
      if (!silent) toast.error('Bitte zuerst eine Website angeben');
      return { ok: false, reason: 'missing' };
    }
    // Zeile kann inzwischen als Duplikat entfernt worden sein
    if (!rowsRef.current.some((r) => r.id === rowId)) {
      return { ok: false, reason: 'missing' };
    }
    const liveRow = rowsRef.current.find((r) => r.id === rowId);
    const prev = contactOverride || liveRow?.contact;
    if (!prev) return { ok: false, reason: 'missing' };

    const ownCtrl =
      !signal && typeof AbortController !== 'undefined' ? new AbortController() : null;
    const abortSignal = signal || ownCtrl?.signal || null;
    if (ownCtrl) aiAbortRef.current = ownCtrl;
    else if (signal && aiAbortRef.current == null && bulkAbortControllerRef.current) {
      aiAbortRef.current = bulkAbortControllerRef.current;
    }
    const isScrapeAborted = () =>
      !!abortSignal?.aborted || !!bulkAbortRef.current;

    const scrapeStartedAt = Date.now();
    aiLogStickToBottomRef.current = true;
    const normalized = normalizeUrl(websiteRaw);
    const aiSel = getPreferredAiSelection();
    setActiveAiInfo(preferredAiInfoFromSelection(aiSel));

    try {
      setScrapingRowId(rowId);
      setAiSearchLive({
        rowId,
        mode: 'scrape',
        current: 'Starte Impressum-Import…',
        steps: [
          {
            ts: Date.now(),
            message: 'Starte Impressum-Import…',
            step: 'start',
          },
        ],
        sources: [],
        provenance: [],
        impressumUrl: '',
        query: normalized,
        rawJson: '',
        startedAt: scrapeStartedAt,
        aiBackend: preferredAiInfoFromSelection(aiSel).label,
        aiModel: aiSel.model,
      });

      const token = localStorage.getItem('authToken') || '';
      const res = await fetch('/api/scrape-impressum', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          website: normalized,
          stream: true,
          backend: aiSel.backend,
          ...(aiSel.model ? { model: aiSel.model } : {}),
        }),
        signal: abortSignal,
      });

      if (!res.ok) {
        let msg = 'Impressum-Abruf fehlgeschlagen';
        try {
          const errBody = await res.json();
          if (errBody?.error) msg = errBody.error;
        } catch (_) {
          /* ignore */
        }
        throw new Error(msg);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('Streaming nicht unterstützt');
      aiReaderRef.current = reader;

      const decoder = new TextDecoder();
      let buffer = '';
      let finalPayload = null;
      let streamError = null;

      const handleEvent = (event, data) => {
        if (isScrapeAborted()) return;
        if (event === 'progress' && data) {
          applyAiInfoFromEvent(data);
          appendAiStep(rowId, data.message || data.step || '…', data.step || 'info', {
            query: data.query,
            sources: data.sources,
            provenance: data.provenance,
            impressumUrl: data.impressumUrl,
            heartbeat: data.heartbeat,
            rawJson: data.rawJson,
            backendLabel: data.backendLabel || data.label,
            model: data.model,
            backend: data.backend,
          });
          if (data.preview && !data.heartbeat) {
            appendAiStep(rowId, `↳ ${data.preview}`, 'ollama_preview', { heartbeat: true });
          } else if (data.preview && data.phase === 'generate') {
            pushAiLive(rowId, (livePrev) => {
              const steps = [...livePrev.steps];
              const lastPreviewIdx = [...steps].map((s) => s.step).lastIndexOf('ollama_preview');
              const line = {
                ts: Date.now(),
                message: `↳ ${data.preview}`,
                step: 'ollama_preview',
                heartbeat: true,
              };
              if (lastPreviewIdx >= 0) steps[lastPreviewIdx] = line;
              else steps.push(line);
              return {
                ...livePrev,
                mode: 'scrape',
                current: data.message || livePrev.current,
                rawJson: data.rawJson != null ? data.rawJson : livePrev.rawJson,
                steps: steps.slice(-40),
              };
            });
          }
        } else if (event === 'done') {
          finalPayload = data;
          if (data?.rawJson || data?.meta?.companyVerify) {
            pushAiLive(rowId, (livePrev) => ({
              ...livePrev,
              mode: 'scrape',
              rawJson: data.rawJson || livePrev.rawJson,
              current: 'Impressum-Import fertig',
            }));
          }
          appendAiStep(rowId, 'Impressum-Import fertig', 'done', {
            rawJson: data?.rawJson,
          });
        } else if (event === 'error') {
          streamError = data?.error || 'Impressum-Abruf fehlgeschlagen';
          // Manche Fehler-Events tragen trotzdem Teildaten
          if (data?.success && data?.data) finalPayload = data;
          appendAiStep(rowId, streamError, 'error');
        } else if (event === 'cancelled') {
          streamError = 'Abgebrochen';
          appendAiStep(rowId, 'Abgebrochen', 'error');
        }
      };

      while (true) {
        if (isScrapeAborted()) {
          try {
            await reader.cancel();
          } catch (_) {
            /* ignore */
          }
          break;
        }
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        for (const chunk of chunks) {
          const lines = chunk.split('\n');
          let event = 'message';
          const dataLines = [];
          for (const line of lines) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;
          let data = null;
          try {
            data = JSON.parse(dataLines.join('\n'));
          } catch (_) {
            continue;
          }
          handleEvent(event, data);
        }
      }

      if (isScrapeAborted()) {
        return { ok: false, reason: 'aborted' };
      }
      if (streamError && !(finalPayload?.success && finalPayload?.data)) {
        if (!silent) toast.error(streamError);
        return { ok: false, reason: 'error', error: streamError };
      }
      if (!finalPayload?.success || !finalPayload?.data) {
        if (!silent) toast.error(finalPayload?.error || streamError || 'Keine Daten gefunden');
        return { ok: false, reason: 'empty', error: finalPayload?.error || streamError };
      }

      if (!rowsRef.current.some((r) => r.id === rowId)) {
        return { ok: false, reason: 'missing' };
      }
      const d = { ...finalPayload.data, website: normalized };
      const filled = await applyContactDataToRow(rowId, prev, d, { preserveEmail });
      const companyVerify = finalPayload.meta?.companyVerify;
      if (!silent) {
        const parts = [];
        if (filled.length) {
          parts.push(
            `Übernommen: ${filled
              .map(
                (k) =>
                  ({
                    name: 'Name',
                    name2: '2. Ansprechpartner',
                    email: 'E-Mail',
                    company: 'Unternehmen',
                    phone: 'Telefon',
                    website: 'Website',
                    notes: 'Notizen/Branche',
                  })[k]
              )
              .join(', ')}`
          );
        } else {
          parts.push('Impressum geladen, aber keine Felder erkannt');
        }
        if (companyVerify?.verified && companyVerify?.changed && companyVerify?.company) {
          parts.push(`Firma KI-geprüft: ${companyVerify.company}`);
        } else if (companyVerify?.verified) {
          parts.push('Firma KI-bestätigt');
        }
        toast.success(parts.join(' · '));
      }
      pushScrapeDurationSample(Date.now() - scrapeStartedAt);
      return { ok: true, filled: filled.length, data: d, companyVerify };
    } catch (e) {
      if (
        bulkAbortRef.current ||
        abortSignal?.aborted ||
        e?.code === 'ERR_CANCELED' ||
        e?.name === 'CanceledError' ||
        e?.name === 'AbortError'
      ) {
        appendAiStep(rowId, 'Abgebrochen', 'error');
        return { ok: false, reason: 'aborted' };
      }
      appendAiStep(rowId, e.message || 'Impressum-Abruf fehlgeschlagen', 'error');
      if (!silent) {
        toast.error(`Fehler beim Abrufen: ${e.response?.data?.error || e.message}`);
      }
      return { ok: false, reason: 'error' };
    } finally {
      aiReaderRef.current = null;
      setScrapingRowId(null);
    }
  };

  const pushAiLive = (rowId, patchOrFn) => {
    setAiSearchLive((prev) => {
      const base =
        prev && prev.rowId === rowId
          ? prev
          : {
              rowId,
              current: '',
              steps: [],
              sources: [],
              provenance: [],
              impressumUrl: '',
              query: '',
              startedAt: Date.now(),
            };
      const next = typeof patchOrFn === 'function' ? patchOrFn(base) : { ...base, ...patchOrFn };
      return next;
    });
  };

  const appendAiStep = (rowId, message, step = 'info', extra = {}) => {
    const ts = Date.now();
    if (extra.backendLabel || extra.model || extra.backend) {
      applyAiInfoFromEvent(extra);
    }
    pushAiLive(rowId, (prev) => {
      // Quellen: nur Website/Impressum der Firmen-Domain — nie Branchen-Suchtreffer
      let nextSources = prev.sources;
      if (Array.isArray(extra.sources) && extra.sources.length) {
        const importOnly = extra.sources.filter(
          (s) => s && (s.engine === 'site' || s.engine === 'impressum') && s.url
        );
        if (importOnly.length) nextSources = importOnly.slice(0, 2);
      }
      // Query nur Website/Impressum — Branchen-Google-Query nicht als „Quelle“ anzeigen
      const isIndustryStep = step === 'industry' || step === 'industry_search';
      const nextQuery = isIndustryStep
        ? prev.query
        : extra.query != null
          ? extra.query
          : prev.query;
      const nextAiBackend =
        extra.backendLabel != null && String(extra.backendLabel).trim()
          ? String(extra.backendLabel).trim()
          : prev.aiBackend;
      const nextAiModel =
        extra.model != null && String(extra.model).trim()
          ? String(extra.model).trim()
          : prev.aiModel;
      const last = prev.steps[prev.steps.length - 1];
      // Keine doppelten identischen Zeilen hintereinander
      if (
        last &&
        String(last.message || '') === String(message || '') &&
        !extra.heartbeat &&
        last.step === step
      ) {
        return {
          ...prev,
          current: message,
          query: nextQuery,
          sources: nextSources,
          provenance: Array.isArray(extra.provenance) ? extra.provenance : prev.provenance,
          impressumUrl: extra.impressumUrl != null ? extra.impressumUrl : prev.impressumUrl,
          rawJson: extra.rawJson != null ? extra.rawJson : prev.rawJson,
          aiBackend: nextAiBackend,
          aiModel: nextAiModel,
        };
      }
      return {
        ...prev,
        current: message,
        query: nextQuery,
        sources: nextSources,
        provenance: Array.isArray(extra.provenance) ? extra.provenance : prev.provenance,
        impressumUrl: extra.impressumUrl != null ? extra.impressumUrl : prev.impressumUrl,
        rawJson: extra.rawJson != null ? extra.rawJson : prev.rawJson,
        aiBackend: nextAiBackend,
        aiModel: nextAiModel,
        steps: [
          ...prev.steps.filter((s) => !(s.heartbeat && step === s.step)),
          {
            ts,
            message,
            step,
            heartbeat: !!extra.heartbeat,
          },
        ].slice(-40),
      };
    });
  };

  const runAiSearchForRow = async (row, { silent = false, signal = null } = {}) => {
    const prev = row?.contact;
    if (!prev) return { ok: false, reason: 'missing' };

    const hasHint =
      String(prev.company || '').trim() ||
      String(prev.name || '').trim() ||
      String(prev.email || '').trim() ||
      String(prev.website || '').trim() ||
      String(prev.notes || '').trim();
    if (!hasHint) {
      if (!silent) toast.error('Bitte zuerst Firma, Name, E-Mail oder Website angeben');
      return { ok: false, reason: 'no-hint' };
    }

    const { source } = resolveGenerateSource(prev);
    if (!source) {
      if (!silent) toast.error('Keine Website oder Firmen-E-Mail für KI-Suche');
      return { ok: false, reason: 'no-source' };
    }

    const ownCtrl =
      !signal && typeof AbortController !== 'undefined' ? new AbortController() : null;
    const abortSignal = signal || ownCtrl?.signal || null;
    aiCancelRequestedRef.current = false;
    if (ownCtrl) aiAbortRef.current = ownCtrl;
    else if (signal && aiAbortRef.current == null && bulkAbortControllerRef.current) {
      aiAbortRef.current = bulkAbortControllerRef.current;
    }
    const isAiAborted = () =>
      !!aiCancelRequestedRef.current ||
      !!abortSignal?.aborted ||
      !!bulkAbortRef.current;
    aiLogStickToBottomRef.current = true;
    const searchStartedAt = Date.now();
    const aiSel = getPreferredAiSelection();
    const prefInfo = preferredAiInfoFromSelection(aiSel);
    setActiveAiInfo(prefInfo);
    setAiSearchingRowId(row.id);
    setAiSearchLive({
      rowId: row.id,
      current: 'Starte KI-Suche…',
      steps: [{ ts: Date.now(), message: 'Starte KI-Suche…', step: 'start' }],
      sources: [],
      provenance: [],
      impressumUrl: '',
      query: '',
      rawJson: '',
      startedAt: searchStartedAt,
      etaAvgMs: estimateAiSearchAvgMs(),
      aiBackend: prefInfo.label,
      aiModel: aiSel.model,
    });

    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch('/api/ai/enrich-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          stream: true,
          backend: aiSel.backend,
          ...(aiSel.model ? { model: aiSel.model } : {}),
          contact: {
            name: prev.name,
            name2: prev.name2,
            email: prev.email,
            company: prev.company,
            phone: prev.phone,
            website: prev.website,
            notes: prev.notes,
          },
        }),
        signal: abortSignal,
      });

      if (!res.ok) {
        let msg = 'KI-Suche fehlgeschlagen';
        try {
          const errBody = await res.json();
          if (errBody?.error) msg = errBody.error;
        } catch (_) {
          /* ignore */
        }
        throw new Error(msg);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('Streaming nicht unterstützt');
      aiReaderRef.current = reader;

      const decoder = new TextDecoder();
      let buffer = '';
      let finalPayload = null;
      let streamError = null;

      const handleEvent = (event, data) => {
        if (isAiAborted()) return;
        if (event === 'progress' && data) {
          applyAiInfoFromEvent(data);
          appendAiStep(row.id, data.message || data.step || '…', data.step || 'info', {
            query: data.query,
            sources: data.sources,
            provenance: data.provenance,
            impressumUrl: data.impressumUrl,
            heartbeat: data.heartbeat,
            rawJson: data.rawJson,
            backendLabel: data.backendLabel || data.label,
            model: data.model,
            backend: data.backend,
          });
          if (data.preview && !data.heartbeat) {
            appendAiStep(row.id, `↳ ${data.preview}`, 'ollama_preview', { heartbeat: true });
          } else if (data.preview && data.phase === 'generate') {
            pushAiLive(row.id, (livePrev) => {
              const steps = [...livePrev.steps];
              const lastPreviewIdx = [...steps].map((s) => s.step).lastIndexOf('ollama_preview');
              const line = {
                ts: Date.now(),
                message: `↳ ${data.preview}`,
                step: 'ollama_preview',
                heartbeat: true,
              };
              if (lastPreviewIdx >= 0) steps[lastPreviewIdx] = line;
              else steps.push(line);
              return {
                ...livePrev,
                current: data.message || livePrev.current,
                rawJson: data.rawJson != null ? data.rawJson : livePrev.rawJson,
                steps: steps.slice(-40),
              };
            });
          }
        } else if (event === 'done') {
          finalPayload = data;
          if (data?.rawJson) {
            pushAiLive(row.id, (livePrev) => ({
              ...livePrev,
              rawJson: data.rawJson,
              current: data?.message || 'KI fertig',
            }));
          }
          appendAiStep(row.id, data?.message || 'KI fertig', 'done', { rawJson: data?.rawJson });
        } else if (event === 'error') {
          streamError = data?.error || 'Anreicherung fehlgeschlagen';
          appendAiStep(row.id, streamError, 'error');
        } else if (event === 'cancelled') {
          streamError = 'Abgebrochen';
          appendAiStep(row.id, 'Abgebrochen', 'error');
        }
      };

      while (true) {
        if (isAiAborted()) {
          try {
            await reader.cancel();
          } catch (_) {
            /* ignore */
          }
          break;
        }
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        for (const chunk of chunks) {
          const lines = chunk.split('\n');
          let event = 'message';
          const dataLines = [];
          for (const line of lines) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;
          let data = null;
          try {
            data = JSON.parse(dataLines.join('\n'));
          } catch (_) {
            continue;
          }
          handleEvent(event, data);
        }
      }

      if (isAiAborted()) {
        return { ok: false, reason: 'aborted' };
      }
      if (streamError) {
        if (!silent) toast.error(streamError);
        return { ok: false, reason: 'error', error: streamError };
      }
      if (!finalPayload?.success || !finalPayload?.data) {
        if (!silent) toast.error(finalPayload?.error || 'KI-Suche ohne Treffer');
        return { ok: false, reason: 'empty', error: finalPayload?.error };
      }

      const ai = finalPayload.data;
      const finalSources = Array.isArray(finalPayload.search?.sources)
        ? finalPayload.search.sources.filter(
            (s) => s && (s.engine === 'site' || s.engine === 'impressum') && s.url
          )
        : [];
      const finalProvenance = Array.isArray(finalPayload.search?.provenance)
        ? finalPayload.search.provenance
        : Array.isArray(finalPayload.provenance)
          ? finalPayload.provenance
          : [];
      appendAiStep(row.id, 'Übernehme KI-Antwort…', 'apply', {
        rawJson: finalPayload.rawJson || '',
        sources: finalSources.length ? finalSources : undefined,
        provenance: finalProvenance,
        impressumUrl: finalPayload.search?.impressumUrl || '',
      });
      if (ai.industry) {
        appendAiStep(row.id, `Branche → Notizen: ${ai.industry}`, 'industry');
      }
      const merged = {
        website: (ai.website || prev.website || '').trim(),
        name: (ai.name || prev.name || '').trim(),
        name2: (ai.name2 || prev.name2 || '').trim(),
        email: (ai.email || prev.email || '').trim(),
        company: (ai.company || prev.company || '').trim(),
        phone: (ai.phone || prev.phone || '').trim(),
        notes:
          ai.notes != null && String(ai.notes).trim()
            ? String(ai.notes).trim()
            : prev.notes || '',
        industry: (ai.industry || '').trim(),
      };

      const filled = await applyContactDataToRow(row.id, prev, merged, { preserveEmail: false });
      appendAiStep(
        row.id,
        filled.length
          ? `Fertig: ${filled.join(', ')} übernommen`
          : 'Fertig — keine neuen Felder',
        'complete'
      );
      if (!silent) {
        toast.success(
          filled.length
            ? `KI-Suche: ${filled
                .map(
                  (k) =>
                    ({
                      name: 'Name',
                      name2: '2. Ansprechpartner',
                      email: 'E-Mail',
                      company: 'Unternehmen',
                      phone: 'Telefon',
                      website: 'Website',
                      notes: 'Notizen/Branche',
                    })[k]
                )
                .join(', ')}`
            : 'KI-Suche abgeschlossen, aber keine neuen Felder'
        );
      }
      return { ok: filled.length > 0, reason: filled.length ? 'ok' : 'empty', filled };
    } catch (e) {
      if (e?.name === 'AbortError' || isAiAborted()) {
        appendAiStep(row.id, 'Abgebrochen', 'error');
        if (!silent && !aiCancelRequestedRef.current) {
          toast('KI-Suche abgebrochen', { icon: '⏹️' });
        }
        return { ok: false, reason: 'aborted' };
      }
      appendAiStep(row.id, e.message || 'KI-Suche fehlgeschlagen', 'error');
      if (!silent) {
        toast.error(e.response?.data?.error || e.message || 'KI-Suche fehlgeschlagen');
      }
      return { ok: false, reason: 'error', error: e.message };
    } finally {
      const elapsed = Date.now() - searchStartedAt;
      if (elapsed >= 1500 && !isAiAborted()) {
        pushAiDurationSample(elapsed);
      }
      aiReaderRef.current = null;
      setAiSearchingRowId(null);
      if (ownCtrl && aiAbortRef.current === ownCtrl) aiAbortRef.current = null;
    }
  };

  const handleAiSearchRow = async (row) => {
    if (importing || bulkGenerating || bulkAiSearching || scrapingRowId != null || aiSearchingRowId != null) {
      return;
    }
    await runAiSearchForRow(row, { silent: false });
  };

  const cancelAiSearch = () => {
    const rowId = aiSearchingRowId || scrapingRowId;
    aiCancelRequestedRef.current = true;
    bulkAbortRef.current = true;
    try {
      aiAbortRef.current?.abort();
    } catch (_) {
      /* ignore */
    }
    try {
      bulkAbortControllerRef.current?.abort();
    } catch (_) {
      /* ignore */
    }
    try {
      aiReaderRef.current?.cancel?.();
    } catch (_) {
      /* ignore */
    }
    aiReaderRef.current = null;
    setAiSearchingRowId(null);
    setScrapingRowId(null);
    if (rowId) {
      appendAiStep(rowId, 'Abgebrochen — gestoppt', 'error');
    }
  };

  const cancelAiSearchAll = () => {
    if (!bulkAiSearching && !aiSearchingRowId) return;
    bulkAbortRef.current = true;
    aiCancelRequestedRef.current = true;
    try {
      bulkAbortControllerRef.current?.abort();
    } catch (_) {
      /* ignore */
    }
    cancelAiSearch();
    setBulkAiSearching(false);
    setBulkAiProgress(null);
  };

  const handleAiSearchAll = async () => {
    if (bulkGenerating || bulkAiSearching || importing || scrapingRowId || aiSearchingRowId) return;

    // Immer von oben (Anzeige / CSV-Index), nicht „irgendwo in der Mitte“ starten
    const orderedRows = [...rows].sort(
      (a, b) => (Number(a.index) || 0) - (Number(b.index) || 0)
    );
    const targets = orderedRows.filter((row) => {
      const { source } = resolveGenerateSource(row.contact);
      return !!source;
    });
    if (!targets.length) {
      toast.error('Keine Zeilen mit Website oder Firmen-E-Mail für KI-Suche');
      return;
    }

    bulkAbortRef.current = false;
    aiCancelRequestedRef.current = false;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    bulkAbortControllerRef.current = controller;
    setBulkAiSearching(true);
    setActiveAiInfo(null);
    setAiSearchLive(null);
    scrollImportListToTop();
    const batchStartedAt = Date.now();
    setBulkAiProgress({
      done: 0,
      total: targets.length,
      startedAt: batchStartedAt,
      currentStartedAt: batchStartedAt,
      avgMs: estimateAiSearchAvgMs(),
    });
    let ok = 0;
    let empty = 0;
    let fail = 0;
    let aborted = false;

    try {
      for (let i = 0; i < targets.length; i += 1) {
        if (bulkAbortRef.current || controller?.signal?.aborted || aiCancelRequestedRef.current) {
          aborted = true;
          break;
        }
        const row = targets[i];
        scrollImportRowIntoView(row.id);
        // Frische Kontaktdaten aus State (vorherige Zeilen können Website gesetzt haben)
        const liveRow =
          (await new Promise((resolve) => {
            setRows((prev) => {
              resolve(prev.find((r) => r.id === row.id) || row);
              return prev;
            });
          })) || row;
        const currentStartedAt = Date.now();
        setBulkAiProgress({
          done: i,
          total: targets.length,
          startedAt: batchStartedAt,
          currentStartedAt,
          avgMs: estimateAiSearchAvgMs(),
        });
        aiAbortRef.current = controller;
        const result = await runAiSearchForRow(liveRow, {
          silent: true,
          signal: controller?.signal || null,
        });
        if (result?.reason === 'aborted' || bulkAbortRef.current || aiCancelRequestedRef.current) {
          aborted = true;
          break;
        }
        if (result?.ok) ok += 1;
        else if (result?.reason === 'empty' || result?.reason === 'no-source') empty += 1;
        else fail += 1;

        if (i < targets.length - 1) {
          const delayAborted = await new Promise((resolve) => {
            const started = Date.now();
            const tick = () => {
              if (bulkAbortRef.current || controller?.signal?.aborted || aiCancelRequestedRef.current) {
                resolve(true);
                return;
              }
              if (Date.now() - started >= 400) {
                resolve(false);
                return;
              }
              window.setTimeout(tick, 40);
            };
            tick();
          });
          if (delayAborted) {
            aborted = true;
            break;
          }
        }
      }
      if (!aborted) {
        setBulkAiProgress({ done: targets.length, total: targets.length });
      }
      const parts = [`${ok} erfolgreich`];
      if (empty) parts.push(`${empty} ohne Treffer`);
      if (fail) parts.push(`${fail} fehlgeschlagen`);
      if (aborted) {
        toast(`KI-Suche abgebrochen (${parts.join(', ')})`, { icon: '⏹️' });
      } else {
        toast.success(`KI-Suche für alle: ${parts.join(', ')}`);
      }
    } finally {
      bulkAbortRef.current = false;
      bulkAbortControllerRef.current = null;
      aiAbortRef.current = null;
      setBulkAiSearching(false);
      setBulkAiProgress(null);
      setAiSearchingRowId(null);
      setActiveAiInfo(null);
      uncheckRowsWithoutEmail();
    }
  };

  const closeAiSearchLive = (rowId = null) => {
    if (aiSearchingRowId && (rowId == null || aiSearchingRowId === rowId)) {
      cancelAiSearch();
    }
    setAiSearchLive((cur) => {
      if (!cur) return null;
      if (rowId != null && cur.rowId !== rowId) return cur;
      return null;
    });
  };

  const resolveGenerateSource = (contact) => {
    const website = String(contact?.website || '').trim();
    if (website) {
      return { source: website, preserveEmail: false, skipReason: null };
    }
    const email = String(contact?.email || '').trim();
    if (!email || !email.includes('@')) {
      return { source: '', preserveEmail: true, skipReason: 'no-source' };
    }
    const domain = emailDomain(email);
    if (!domain) {
      return { source: '', preserveEmail: true, skipReason: 'invalid-email' };
    }
    if (detectMailProvider(email) || FREE_EMAIL_DOMAINS.has(domain)) {
      return { source: '', preserveEmail: true, skipReason: 'freemail' };
    }
    return { source: domain, preserveEmail: true, skipReason: null };
  };

  const handleGenerateFromWebsite = (row) => {
    scrapeImpressumIntoRow(row.id, row.contact.website);
  };

  const handleGenerateFromEmail = (row) => {
    const email = String(row.contact.email || '').trim();
    if (!email || !email.includes('@')) {
      toast.error('Bitte zuerst eine E-Mail angeben');
      return;
    }
    const domain = emailDomain(email);
    if (!domain) {
      toast.error('Ungültige E-Mail-Adresse');
      return;
    }
    if (detectMailProvider(email) || FREE_EMAIL_DOMAINS.has(domain)) {
      toast.error('Bei Freemail-Adressen (Gmail, GMX, …) keine Firmenwebsite ableitbar');
      return;
    }
    return scrapeImpressumIntoRow(row.id, domain, { preserveEmail: true });
  };

  const cancelGenerateAll = () => {
    if (!bulkGenerating) return;
    bulkAbortRef.current = true;
    try {
      bulkAbortControllerRef.current?.abort();
    } catch (_) {
      /* ignore */
    }
  };

  const handleGenerateAll = async () => {
    if (bulkGenerating || bulkAiSearching || importing || scrapingRowId || aiSearchingRowId) return;
    const targets = rows.filter((row) => {
      const { source } = resolveGenerateSource(row.contact);
      return !!source;
    });
    if (!targets.length) {
      toast.error('Keine Zeilen mit Website oder Firmen-E-Mail zum Generieren');
      return;
    }

    bulkAbortRef.current = false;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    bulkAbortControllerRef.current = controller;
    setBulkGenerating(true);
    setActiveAiInfo(null);
    const batchStartedAt = Date.now();
    setBulkGenerateProgress({
      done: 0,
      total: targets.length,
      startedAt: batchStartedAt,
      currentStartedAt: batchStartedAt,
      avgMs: estimateScrapeAvgMs(),
    });
    let ok = 0;
    let empty = 0;
    let fail = 0;
    let aborted = false;

    try {
      for (let i = 0; i < targets.length; i += 1) {
        if (bulkAbortRef.current || controller?.signal?.aborted) {
          aborted = true;
          break;
        }
        const row = targets[i];
        // Zwischenzeitlich als E-Mail-/Firmen-Duplikat entfernt?
        if (!rowsRef.current.some((r) => r.id === row.id)) {
          setBulkGenerateProgress({
            done: i + 1,
            total: targets.length,
            startedAt: batchStartedAt,
            currentStartedAt: Date.now(),
            avgMs: estimateScrapeAvgMs(),
          });
          continue;
        }
        const currentStartedAt = Date.now();
        setBulkGenerateProgress({
          done: i,
          total: targets.length,
          startedAt: batchStartedAt,
          currentStartedAt,
          avgMs: estimateScrapeAvgMs(),
        });
        scrollImportRowIntoView(row.id);
        const { source, preserveEmail } = resolveGenerateSource(row.contact);
        if (!source) continue;
        const result = await scrapeImpressumIntoRow(row.id, source, {
          preserveEmail,
          silent: true,
          contactOverride: row.contact,
          signal: controller?.signal || null,
        });
        if (result?.reason === 'aborted' || bulkAbortRef.current) {
          aborted = true;
          break;
        }
        if (result?.ok) ok += 1;
        else if (result?.reason === 'empty') empty += 1;
        else fail += 1;
        if (i < targets.length - 1) {
          const delayAborted = await new Promise((resolve) => {
            const started = Date.now();
            const tick = () => {
              if (bulkAbortRef.current || controller?.signal?.aborted) {
                resolve(true);
                return;
              }
              if (Date.now() - started >= 250) {
                resolve(false);
                return;
              }
              window.setTimeout(tick, 40);
            };
            tick();
          });
          if (delayAborted) {
            aborted = true;
            break;
          }
        }
      }
      if (!aborted) {
        setBulkGenerateProgress({
          done: targets.length,
          total: targets.length,
          startedAt: batchStartedAt,
          currentStartedAt: Date.now(),
          avgMs: estimateScrapeAvgMs(),
        });
      }
      const parts = [`${ok} erfolgreich`];
      if (empty) parts.push(`${empty} ohne Treffer`);
      if (fail) parts.push(`${fail} fehlgeschlagen`);
      if (aborted) {
        toast(`Generieren abgebrochen (${parts.join(', ')})`, { icon: '⏹️' });
      } else {
        toast.success(`Generieren abgeschlossen: ${parts.join(', ')}`);
      }
    } finally {
      bulkAbortRef.current = false;
      bulkAbortControllerRef.current = null;
      setBulkGenerating(false);
      setBulkGenerateProgress(null);
      setScrapingRowId(null);
      setActiveAiInfo(null);
      uncheckRowsWithoutEmail();
    }
  };

  useEffect(() => {
    if (!open || !isWebsiteMode) return undefined;
    if (autoWebsiteScrapeRef.current) return undefined;
    if (!rows.length) return undefined;
    if (bulkGenerating || bulkAiSearching || importing || scrapingRowId != null || aiSearchingRowId != null) {
      return undefined;
    }
    autoWebsiteScrapeRef.current = true;
    const timer = window.setTimeout(() => {
      handleGenerateAll();
    }, 400);
    return () => window.clearTimeout(timer);
    // Einmalig nach Öffnen / Zeilenaufbau
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isWebsiteMode, rows.length]);

  const toggleRowCategory = (rowId, categoryId) => {
    const id = Number(categoryId);
    if (!id) return;
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const current = normalizeCategoryIds(row.contact.categoryIds);
        const next = current.includes(id)
          ? current.filter((x) => x !== id)
          : [...current, id];
        return { ...row, contact: { ...row.contact, categoryIds: next } };
      })
    );
  };

  const toggleBulkCategory = (categoryId) => {
    const id = Number(categoryId);
    if (!id) return;
    setBulkCategoryIds((prev) => {
      const current = normalizeCategoryIds(prev);
      return current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
    });
  };

  const applyBulkCategoriesToSelected = () => {
    const ids = normalizeCategoryIds(bulkCategoryIds);
    if (!ids.length) {
      toast.error('Bitte zuerst Kategorien auswählen');
      return;
    }
    let applied = 0;
    setRows((prev) =>
      prev.map((row) => {
        if (!row.selected) return row;
        applied += 1;
        return {
          ...row,
          contact: { ...row.contact, categoryIds: [...ids] },
        };
      })
    );
    if (!applied) {
      toast.error('Keine Kontakte ausgewählt');
      return;
    }
    toast.success(
      applied === 1
        ? 'Kategorien für 1 Kontakt gesetzt'
        : `Kategorien für ${applied} Kontakte gesetzt`
    );
  };

  const toggleRow = (id) => {
    setRows((prev) => {
      const target = prev.find((r) => r.id === id);
      if (!target) return prev;

      // Abwählen — immer erlaubt
      if (target.selected) {
        return prev.map((r) =>
          r.id === id ? { ...r, selected: false, forceInclude: false } : r
        );
      }

      // Anwählen — immer erlaubt (auch ohne E-Mail/Branche / bei Duplikat)
      let cleared = 0;
      const next = prev.map((r) => {
        if (r.id === id) {
          return {
            ...r,
            selected: true,
            forceInclude: true,
          };
        }
        // Keine Duplikate im ausgewählten Pool
        if (r.selected && rowsConflict(r, target)) {
          cleared += 1;
          return { ...r, selected: false, forceInclude: false };
        }
        return r;
      });

      if (!normalizeEmail(target.contact.email)) {
        queueMicrotask(() =>
          toast('Ausgewählt — zum Import fehlt noch eine E-Mail', { icon: 'ℹ️' })
        );
      } else if (!isEmailWebsiteMode && !contactHasIndustry(target.contact)) {
        queueMicrotask(() =>
          toast('Ausgewählt — zum Import fehlt noch eine Kategorie', { icon: 'ℹ️' })
        );
      } else if (cleared > 0) {
        queueMicrotask(() =>
          toast.success(
            cleared === 1
              ? '1 konkurrierende Auswahl abgewählt (kein Duplikat im Pool)'
              : `${cleared} konkurrierende Auswahlen abgewählt (kein Duplikat im Pool)`
          )
        );
      }

      return next;
    });
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleCreateCategory = async () => {
    const name = String(newCategoryName || '').trim();
    if (!name) {
      toast.error('Name für die Kategorie fehlt');
      return;
    }
    setCategoryBusy(true);
    try {
      const res = await axios.post('/api/contact-categories', {
        name,
        color: newCategoryColor,
      });
      const created = res.data?.category;
      if (created?.id) {
        setCategories((prev) =>
          [...prev, created].sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' })
          )
        );
        setBulkCategoryIds((prev) => [...normalizeCategoryIds(prev), Number(created.id)]);
      }
      setNewCategoryName('');
      setNewCategoryColor(
        CATEGORY_COLOR_PRESETS[(categories.length + 1) % CATEGORY_COLOR_PRESETS.length]
      );
      setShowNewCategory(false);
      toast.success('Kategorie erstellt');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Kategorie konnte nicht erstellt werden');
    } finally {
      setCategoryBusy(false);
    }
  };

  const importSelected = async () => {
    // Sicherheit: auch beim Absenden nochmal deduplizieren
    const selected = rows.filter((r) => {
      if (!r.selected || r.alreadyImported) return false;
      if (isEmailWebsiteMode) {
        // E-Mail reicht — Kategorie/Website blockieren den Import nicht
        return Boolean(normalizeEmail(r.contact?.email));
      }
      return rowHasImportBasics(r.contact, basicsOpts);
    });
    const ordered = [...selected].sort(
      (a, b) =>
        Number(b.forceInclude) - Number(a.forceInclude) ||
        contactCompletenessScore(b.contact) - contactCompletenessScore(a.contact) ||
        (a.index ?? 0) - (b.index ?? 0)
    );
    const seenEmails = new Set();
    const seenCompanies = new Set();
    const seenNames = new Set();
    const unique = [];
    for (const r of ordered) {
      const email = normalizeEmail(r.contact.email);
      const companyKey = normalizeCompanyKey(r.contact.company);
      const nameKey = normalizePersonNameKey(r.contact.name);
      if (email && seenEmails.has(email)) continue;
      if (!isEmailWebsiteMode) {
        if (isStrongCompanyKey(companyKey) && seenCompanies.has(companyKey)) continue;
        if (nameKey.length >= 3 && seenNames.has(nameKey)) continue;
      }
      if (email) seenEmails.add(email);
      if (isStrongCompanyKey(companyKey)) seenCompanies.add(companyKey);
      if (nameKey.length >= 3) seenNames.add(nameKey);
      unique.push(r);
    }

    const toImport = unique.map((r) => ({
        name: r.contact.name,
        email: normalizeEmail(r.contact.email),
        company: r.contact.company,
        phone: r.contact.phone,
        website: r.contact.website,
        notes: r.contact.notes,
        gender: r.contact.gender || 'unknown',
        name2: r.contact.name2 || '',
        gender2: r.contact.gender2 || 'unknown',
        categoryIds: normalizeCategoryIds(r.contact.categoryIds),
      }));

    if (!toImport.length) {
      toast.error(
        isEmailWebsiteMode
          ? 'Keine gültigen Kontakte ausgewählt (E-Mail nötig)'
          : 'Keine gültigen Kontakte ausgewählt (E-Mail + Kategorie nötig)'
      );
      return;
    }
    if (unique.length < selected.length) {
      toast(
        `${selected.length - unique.length} Duplikat(e) in der Auswahl übersprungen`,
        { icon: 'ℹ️' }
      );
    }

    setImporting(true);
    setImportProgress({ done: 0, total: toImport.length });
    const CHUNK_SIZE = 100;
    let created = 0;
    let skipped = 0;
    const allIds = [];
    try {
      for (let offset = 0; offset < toImport.length; offset += CHUNK_SIZE) {
        const chunk = toImport.slice(offset, offset + CHUNK_SIZE);
        const res = await axios.post(
          '/api/contacts/bulk',
          { contacts: chunk },
          { timeout: 120000 }
        );
        created += Number(res.data?.created) || 0;
        skipped += Number(res.data?.skipped) || 0;
        if (Array.isArray(res.data?.ids)) allIds.push(...res.data.ids);
        setImportProgress({
          done: Math.min(offset + chunk.length, toImport.length),
          total: toImport.length,
        });
      }
      onImported?.({ created, skipped, ids: allIds });
      toast.success(
        skipped
          ? `${created} importiert, ${skipped} übersprungen`
          : `${created} Kontakte importiert`
      );
      onClose?.();
    } catch (error) {
      if (created > 0) {
        onImported?.({ created, skipped, ids: allIds });
        toast.error(
          `Teilweise importiert (${created} von ${toImport.length}). ${
            error.response?.data?.error || error.message || 'Import abgebrochen'
          }`
        );
        onClose?.();
      } else {
        toast.error(error.response?.data?.error || 'Import fehlgeschlagen');
      }
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  const renderCategoryChips = ({
    selectedIds,
    onToggle,
    disabled = false,
    collapsible = false,
    expanded = true,
    onToggleExpanded,
  }) => {
    if (!categories.length) {
      return <p className="text-xs text-neutral-500">Noch keine Kategorien vorhanden.</p>;
    }
    const active = new Set(normalizeCategoryIds(selectedIds));
    const selectedCats = categories.filter((cat) => active.has(Number(cat.id)));
    const showAll = !collapsible || expanded;
    const visible = showAll ? categories : selectedCats;
    return (
      <div className="space-y-1.5">
        {collapsible ? (
          <button
            type="button"
            onClick={() => onToggleExpanded?.()}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
          >
            {expanded ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
            {expanded
              ? 'Kategorien einklappen'
              : selectedCats.length
                ? `${selectedCats.length} gewählt · aufklappen`
                : 'Kategorien aufklappen'}
          </button>
        ) : null}
        {!showAll && !selectedCats.length ? (
          <p className="text-[11px] text-neutral-500">Keine Kategorie gewählt.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-0.5">
            {visible.map((cat) => {
              const isOn = active.has(Number(cat.id));
              return (
                <button
                  key={cat.id}
                  type="button"
                  disabled={disabled || categoryBusy}
                  onClick={() => onToggle(cat.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border transition-colors disabled:opacity-50 ${
                    isOn
                      ? 'border-transparent text-white'
                      : 'border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                  }`}
                  style={isOn ? { backgroundColor: cat.color || '#0F766E' } : undefined}
                >
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      isOn ? 'border border-black' : ''
                    }`}
                    style={{ backgroundColor: cat.color || '#0F766E' }}
                  />
                  {cat.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-neutral-950/60 backdrop-blur-sm">
      <div className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
              {isWebsiteMode || isEmailWebsiteMode ? (
                <Globe className="w-5 h-5 text-primary-600" />
              ) : (
                <Upload className="w-5 h-5 text-primary-600" />
              )}
              {isWebsiteMode
                ? 'Website-Import'
                : isEmailWebsiteMode
                  ? 'E-Mail + Website'
                  : 'Import prüfen'}
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
              {isWebsiteMode
                ? fileName
                  ? `Datei „${fileName}“ · nur Impressum · Firma per KI gegengeprüft`
                  : 'Nur Impressum · Firmenname wird per KI gegengeprüft'
                : isEmailWebsiteMode
                  ? fileName
                    ? `Datei „${fileName}“ · nur E-Mail & Website · vorhandene abgewählt`
                    : 'Nur E-Mail und Website · bereits vorhandene Kontakte abgewählt'
                  : fileName
                    ? `Datei „${fileName}“ · Daten & Kategorien vor dem Import korrigieren`
                    : 'Daten & Kategorien vor dem Import korrigieren'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={importing || bulkGenerating || bulkAiSearching}
            className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-neutral-100 dark:border-neutral-800 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              {rows.length} Zeilen · {selectedCount} zum Import ausgewählt
              {droppedDupesInfo ? (
                <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  Duplikate: {droppedDupesInfo}
                </span>
              ) : null}
            </p>
            <div className="flex gap-2 flex-wrap">
              {bulkGenerating ? (
                <>
                  <span className="inline-flex items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-300 px-1 max-w-[min(100%,28rem)]">
                    <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
                    <span className="truncate">
                      {bulkGenerateProgress
                        ? `Generieren… ${Math.min(bulkGenerateProgress.done + 1, bulkGenerateProgress.total)}/${bulkGenerateProgress.total}`
                        : 'Generieren…'}
                      {bulkGenerateEtaLabel ? ` · ${bulkGenerateEtaLabel}` : ''}
                      {formatAiInfoLabel(activeAiInfo) ? ` · ${formatAiInfoLabel(activeAiInfo)}` : ''}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn-secondary text-xs !py-1.5 !text-danger-700 dark:text-danger-300 border-danger-200 dark:border-danger-900/50"
                    onClick={cancelGenerateAll}
                  >
                    Abbrechen
                  </button>
                </>
              ) : bulkAiSearching ? (
                <>
                  <span className="inline-flex items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-300 px-1 max-w-[min(100%,28rem)]">
                    <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
                    <span className="truncate">
                      {bulkAiProgress
                        ? `KI-Suche… ${Math.min(bulkAiProgress.done + 1, bulkAiProgress.total)}/${bulkAiProgress.total}`
                        : 'KI-Suche…'}
                      {bulkAiEtaLabel ? ` · ${bulkAiEtaLabel}` : ''}
                      {formatAiInfoLabel(activeAiInfo) ? ` · ${formatAiInfoLabel(activeAiInfo)}` : ''}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn-secondary text-xs !py-1.5 !text-danger-700 dark:text-danger-300 border-danger-200 dark:border-danger-900/50"
                    onClick={cancelAiSearchAll}
                  >
                    Abbrechen
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-secondary text-xs !py-1.5 inline-flex items-center gap-1"
                    disabled={importing || scrapingRowId != null || aiSearchingRowId != null}
                    onClick={handleGenerateAll}
                    title="Impressum-Daten für alle Zeilen mit Website oder Firmen-E-Mail abrufen"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Alle generieren
                  </button>
                  <button
                    type="button"
                    className="btn-secondary text-xs !py-1.5 inline-flex items-center gap-1"
                    disabled={importing || scrapingRowId != null || aiSearchingRowId != null}
                    onClick={handleAiSearchAll}
                    title="KI-Suche von oben nach unten für alle Zeilen mit Website oder Firmen-E-Mail"
                  >
                    <Globe className="w-3.5 h-3.5" />
                    Alle KI-Suchen
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn-secondary text-xs !py-1.5"
                disabled={importing || bulkGenerating || bulkAiSearching}
                onClick={() =>
                  setRows((prev) =>
                    syncRowsSelectionState(
                      prev.map((r) => ({
                        ...r,
                        selected: isEmailWebsiteMode
                          ? !r.alreadyImported
                          : (r.forceInclude && rowHasImportBasics(r.contact, basicsOpts)) ||
                            rowCanBeSelected(r.contact, {
                              ...basicsOpts,
                              alreadyImported: r.alreadyImported,
                              duplicate: r.duplicate,
                            }),
                        forceInclude: isEmailWebsiteMode ? false : r.forceInclude,
                      }))
                    ).rows
                  )
                }
              >
                Alle gültigen wählen
              </button>
              <button
                type="button"
                className="btn-secondary text-xs !py-1.5"
                disabled={importing || bulkGenerating || bulkAiSearching}
                onClick={() =>
                  setRows((prev) =>
                    prev.map((r) => ({ ...r, selected: false, forceInclude: false }))
                  )
                }
              >
                Auswahl leeren
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50/80 dark:bg-neutral-950/40 px-3 py-2.5 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-medium text-neutral-700 dark:text-neutral-200">
                Kategorien für alle ausgewählten
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={categoryBusy || importing}
                  onClick={() => setShowNewCategory((v) => !v)}
                  className="inline-flex items-center gap-0.5 text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 disabled:opacity-50"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Neu
                </button>
                <button
                  type="button"
                  disabled={importing || bulkGenerating || bulkAiSearching || selectedCount === 0 || bulkCategoryIds.length === 0}
                  onClick={applyBulkCategoriesToSelected}
                  className="text-[11px] font-medium px-2 py-1 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  Allen zuweisen
                </button>
              </div>
            </div>
            {renderCategoryChips({
              selectedIds: bulkCategoryIds,
              onToggle: toggleBulkCategory,
              disabled: importing,
              collapsible: true,
              expanded: categoriesExpanded,
              onToggleExpanded: () => setCategoriesExpanded((v) => !v),
            })}
            {showNewCategory ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                <input
                  type="text"
                  value={newCategoryName}
                  disabled={categoryBusy || importing}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateCategory();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setShowNewCategory(false);
                      setNewCategoryName('');
                    }
                  }}
                  className="input-field !py-1.5 !px-2.5 text-xs w-40 min-w-0"
                  placeholder="Neue Kategorie"
                  autoFocus
                />
                <div className="flex items-center gap-1">
                  {CATEGORY_COLOR_PRESETS.slice(0, 6).map((color) => (
                    <button
                      key={color}
                      type="button"
                      disabled={categoryBusy || importing}
                      onClick={() => setNewCategoryColor(color)}
                      className={`w-3.5 h-3.5 rounded-full border ${
                        newCategoryColor === color
                          ? 'border-black'
                          : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      aria-label={`Farbe ${color}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  disabled={categoryBusy || importing || !String(newCategoryName || '').trim()}
                  onClick={handleCreateCategory}
                  className="text-[11px] font-medium text-primary-600 disabled:opacity-50"
                >
                  Anlegen
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div ref={importListRef} className="flex-1 overflow-y-auto p-5 space-y-3">
          {rows.length === 0 ? (
            <p className="text-center py-16 text-sm text-neutral-500">Keine Zeilen in der Vorschau</p>
          ) : (
            [...rows]
              .sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0))
              .map((row) => (
              <div
                key={row.id}
                data-import-row-id={row.id}
                className={`rounded-xl border p-3 sm:p-4 ${
                  row.alreadyImported || row.duplicate
                    ? 'border-amber-300 dark:border-amber-700/60 bg-amber-50/40 dark:bg-amber-950/10'
                    : row.selected
                    ? 'border-primary-300 dark:border-primary-700 bg-primary-50/30 dark:bg-primary-950/10'
                    : 'border-neutral-200 dark:border-neutral-700'
                } ${
                  aiSearchingRowId === row.id || scrapingRowId === row.id
                    ? 'ring-2 ring-primary-400/60 dark:ring-emerald-500/40'
                    : ''
                }`}
              >
                <div className="flex flex-wrap items-center gap-2 justify-between mb-3">
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={Boolean(row.selected)}
                      onChange={() => toggleRow(row.id)}
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                      data-row-select="always"
                      title="Immer an- und abwählbar"
                    />
                    <span>
                      Zeile {row.index + 1}
                      {!normalizeEmail(row.contact.email) ? (
                        <span className="ml-1.5 text-[11px] font-normal text-amber-700 dark:text-amber-300">
                          (ohne E-Mail)
                        </span>
                      ) : !contactHasIndustry(row.contact) ? (
                        <span className="ml-1.5 text-[11px] font-normal text-amber-700 dark:text-amber-300">
                          (ohne Kategorie)
                        </span>
                      ) : row.forceInclude && (row.alreadyImported || row.duplicate) ? (
                        <span className="ml-1.5 text-[11px] font-normal text-primary-700 dark:text-primary-300">
                          (manuell gewählt)
                        </span>
                      ) : row.alreadyImported ? (
                        <span className="ml-1.5 text-[11px] font-normal text-amber-700 dark:text-amber-300">
                          (bereits importiert)
                        </span>
                      ) : row.duplicate ? (
                        <span className="ml-1.5 text-[11px] font-normal text-amber-700 dark:text-amber-300">
                          (Duplikat)
                        </span>
                      ) : null}
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={importing}
                      onClick={() => removeRow(row.id)}
                      className="p-1.5 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50"
                      title="Zeile entfernen"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {(row.warnings || []).length > 0 ? (
                  <div className="mb-3 text-xs text-amber-700 dark:text-amber-300 flex flex-wrap gap-x-3 gap-y-1">
                    {row.warnings.map((w) => (
                      <span key={w} className="inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {w}
                      </span>
                    ))}
                  </div>
                ) : null}

                {aiSearchLive?.rowId === row.id ? (
                  <div className="mb-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-950/60 overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-neutral-200 dark:border-neutral-800">
                      <div className="min-w-0 flex items-center gap-2">
                        {aiSearchingRowId === row.id || scrapingRowId === row.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-600 flex-shrink-0" />
                        ) : (
                          <Globe className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-[11px] font-medium text-neutral-800 dark:text-neutral-200 truncate">
                            {aiSearchLive.mode === 'scrape'
                              ? scrapingRowId === row.id
                                ? 'Impressum / KI läuft'
                                : 'Impressum / KI'
                              : aiSearchingRowId === row.id
                                ? 'KI-Suche läuft'
                                : 'KI-Suche'}
                            {aiSearchLive.startedAt
                              ? ` · ${Math.max(
                                  0,
                                  Math.floor((Date.now() - aiSearchLive.startedAt) / 1000) + aiSearchTick * 0
                                )}s`
                              : ''}
                            {aiSearchingRowId === row.id && singleAiEtaLabel
                              ? ` · ${singleAiEtaLabel}`
                              : ''}
                            {scrapingRowId === row.id && singleScrapeEtaLabel
                              ? ` · ${singleScrapeEtaLabel}`
                              : ''}
                          </p>
                          {(aiSearchLive.aiBackend || aiSearchLive.aiModel || activeAiInfo) ? (
                            <p className="text-[10px] text-emerald-700 dark:text-emerald-300/90 truncate">
                              KI:{' '}
                              {formatAiInfoLabel({
                                label: aiSearchLive.aiBackend || activeAiInfo?.label,
                                model: aiSearchLive.aiModel || activeAiInfo?.model,
                              }) || '—'}
                            </p>
                          ) : null}
                          <p className="text-[11px] text-neutral-500 truncate">
                            {aiSearchLive.current || '…'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {aiSearchingRowId === row.id || scrapingRowId === row.id ? (
                          <button
                            type="button"
                            onClick={cancelAiSearch}
                            className="text-[11px] px-2 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300 hover:bg-white dark:hover:bg-neutral-900"
                          >
                            Abbrechen
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => closeAiSearchLive(row.id)}
                          className="p-1 rounded-lg text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-200/60 dark:hover:bg-neutral-800"
                          title="Panel schließen"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {aiSearchLive.query ? (
                      <p className="px-3 pt-2 text-[10px] text-neutral-400 truncate">
                        Website: {aiSearchLive.query}
                      </p>
                    ) : null}
                    <div
                      ref={aiLogRef}
                      onScroll={(e) => {
                        const el = e.currentTarget;
                        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                        aiLogStickToBottomRef.current = distanceFromBottom < 28;
                      }}
                      className="max-h-36 overflow-y-auto px-3 py-2 space-y-1 font-mono text-[10px] leading-relaxed text-neutral-600 dark:text-neutral-400"
                    >
                      {(aiSearchLive.steps || []).map((s, idx) => (
                        <div
                          key={`${s.ts}-${idx}`}
                          className={
                            s.step === 'error'
                              ? 'text-danger-600 dark:text-danger-400'
                              : s.step === 'complete' || s.step === 'done'
                                ? 'text-success-700 dark:text-success-400'
                                : s.heartbeat
                                  ? 'text-neutral-400'
                                  : ''
                          }
                        >
                          <span className="text-neutral-400 mr-1.5">
                            {new Date(s.ts).toLocaleTimeString('de-DE', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                            })}
                          </span>
                          {s.message}
                        </div>
                      ))}
                    </div>
                    {(aiSearchLive.sources || []).length ? (
                      <div className="px-3 pb-2 border-t border-neutral-200 dark:border-neutral-800 pt-2">
                        <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-1">
                          Kontaktdaten-Quellen
                        </p>
                        <p className="text-[10px] text-neutral-500 mb-1.5">
                          Name, Firma, E-Mail, Telefon kommen aus dem Impressum.
                        </p>
                        <ul className="space-y-0.5">
                          {aiSearchLive.sources.slice(0, 2).map((src) => (
                            <li key={`${src.engine}-${src.url}`} className="text-[10px] text-neutral-500 truncate">
                              <span className="text-neutral-400 mr-1">
                                {src.engine === 'impressum' ? 'Impressum' : 'Startseite'}:
                              </span>
                              <a
                                href={src.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-neutral-800 dark:hover:text-neutral-200"
                              >
                                {src.url}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {(aiSearchLive.provenance || []).length ? (
                      <div className="px-3 pb-2 border-t border-neutral-200 dark:border-neutral-800 pt-2">
                        <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-1">
                          Feld-Herkunft
                        </p>
                        <ul className="space-y-1">
                          {aiSearchLive.provenance.map((p) => (
                            <li key={p.field} className="text-[10px] text-neutral-600 dark:text-neutral-400">
                              <span className="font-medium text-neutral-700 dark:text-neutral-300">
                                {p.label}
                              </span>
                              {': '}
                              <span className="text-neutral-500">{p.value}</span>
                              <span className="block text-neutral-400 truncate">
                                {p.origin === 'impressum'
                                  ? '← Impressum'
                                  : p.origin === 'website'
                                    ? '← Startseite / Domain'
                                    : p.origin === 'google'
                                      ? '← Branche (extern)'
                                      : '← Importzeile'}
                                {p.url ? (
                                  <>
                                    {' · '}
                                    <a
                                      href={p.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="hover:text-neutral-700 dark:hover:text-neutral-200"
                                    >
                                      öffnen
                                    </a>
                                  </>
                                ) : null}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {aiSearchLive.rawJson ? (
                      <div className="px-3 pb-3 border-t border-neutral-200 dark:border-neutral-800 pt-2">
                        <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-1">KI-JSON</p>
                        <pre className="max-h-40 overflow-auto rounded-lg bg-neutral-900 text-neutral-100 dark:bg-black/60 px-2.5 py-2 text-[10px] leading-relaxed whitespace-pre-wrap break-words">
                          {aiSearchLive.rawJson}
                        </pre>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="space-y-2.5">
                  <div className={`grid gap-2 items-end ${String(row.contact.name || '').trim() ? 'sm:grid-cols-[minmax(0,1fr)_11.5rem]' : 'grid-cols-1'}`}>
                    <div>
                      <label className="block text-[11px] text-neutral-500 mb-0.5">Ansprechpartner</label>
                      <input
                        type="text"
                        className="input-field !py-1.5 !text-sm"
                        value={row.contact.name || ''}
                        disabled={importing}
                        onChange={(e) => {
                          const name = e.target.value;
                          const patch = { name };
                          if (String(name || '').trim()) {
                            patch.gender = guessGenderFromName(name, existingContacts);
                          } else {
                            patch.gender = 'unknown';
                          }
                          updateRowContact(row.id, patch);
                        }}
                      />
                    </div>
                    {String(row.contact.name || '').trim() ? (
                      <div>
                        <label className="block text-[11px] text-neutral-500 mb-0.5">Geschlecht</label>
                        <SelectField
                          value={row.contact.gender || 'unknown'}
                          disabled={importing}
                          onChange={(e) => updateRowContact(row.id, { gender: e.target.value })}
                          className="input-field !py-1.5 !text-sm"
                        >
                          <option value="unknown">Neutral</option>
                          <option value="male">Männlich</option>
                          <option value="female">Weiblich</option>
                        </SelectField>
                      </div>
                    ) : null}
                  </div>

                  <div className={`grid gap-2 items-end ${String(row.contact.name2 || '').trim() ? 'sm:grid-cols-[minmax(0,1fr)_11.5rem]' : 'grid-cols-1'}`}>
                    <div>
                      <label className="block text-[11px] text-neutral-500 mb-0.5">2. Ansprechpartner</label>
                      <input
                        type="text"
                        className="input-field !py-1.5 !text-sm"
                        value={row.contact.name2 || ''}
                        disabled={importing}
                        onChange={(e) => {
                          const name2 = e.target.value;
                          const patch = { name2 };
                          if (String(name2 || '').trim()) {
                            patch.gender2 = guessGenderFromName(name2, existingContacts);
                          } else {
                            patch.gender2 = 'unknown';
                          }
                          updateRowContact(row.id, patch);
                        }}
                      />
                    </div>
                    {String(row.contact.name2 || '').trim() ? (
                      <div>
                        <label className="block text-[11px] text-neutral-500 mb-0.5">Geschlecht</label>
                        <SelectField
                          value={row.contact.gender2 || 'unknown'}
                          disabled={importing}
                          onChange={(e) => updateRowContact(row.id, { gender2: e.target.value })}
                          className="input-field !py-1.5 !text-sm"
                        >
                          <option value="unknown">Neutral</option>
                          <option value="male">Männlich</option>
                          <option value="female">Weiblich</option>
                        </SelectField>
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-[11px] text-neutral-500 mb-0.5">E-Mail *</label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="email"
                        className="input-field !py-1.5 !text-sm flex-1 min-w-0"
                        value={row.contact.email || ''}
                        disabled={importing}
                        onChange={(e) => updateRowContact(row.id, { email: e.target.value })}
                      />
                      <button
                        type="button"
                        title="Daten aus E-Mail-Domain generieren (Impressum)"
                        disabled={
                          importing ||
                          bulkGenerating ||
                          bulkAiSearching ||
                          scrapingRowId != null ||
                          aiSearchingRowId != null
                        }
                        onClick={() => handleGenerateFromEmail(row)}
                        className="px-2 py-1.5 text-[11px] rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 transition-colors inline-flex items-center gap-1 flex-shrink-0"
                      >
                        {scrapingRowId === row.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Sparkles className="w-3 h-3" />
                        )}
                        Generieren
                      </button>
                      <button
                        type="button"
                        title="Per KI im Web suchen und Kontaktfelder füllen"
                        disabled={
                          importing ||
                          bulkGenerating ||
                          bulkAiSearching ||
                          scrapingRowId != null ||
                          aiSearchingRowId != null
                        }
                        onClick={() => handleAiSearchRow(row)}
                        className="px-2 py-1.5 text-[11px] rounded-lg border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-60 transition-colors inline-flex items-center gap-1 flex-shrink-0"
                      >
                        {aiSearchingRowId === row.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Globe className="w-3 h-3" />
                        )}
                        KI-Suche
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] text-neutral-500 mb-0.5">Unternehmen</label>
                    <input
                      type="text"
                      className="input-field !py-1.5 !text-sm"
                      value={row.contact.company || ''}
                      disabled={importing}
                      onChange={(e) => updateRowContact(row.id, { company: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-neutral-500 mb-0.5">Telefon</label>
                    <input
                      type="tel"
                      className="input-field !py-1.5 !text-sm"
                      value={row.contact.phone || ''}
                      disabled={importing}
                      onChange={(e) => updateRowContact(row.id, { phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-neutral-500 mb-0.5">Website</label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        className="input-field !py-1.5 !text-sm flex-1 min-w-0"
                        value={row.contact.website || ''}
                        disabled={importing}
                        placeholder="example.com"
                        onChange={(e) => updateRowContact(row.id, { website: e.target.value })}
                      />
                      <button
                        type="button"
                        title="Daten aus Website (Impressum) generieren"
                        disabled={
                          importing ||
                          bulkGenerating ||
                          bulkAiSearching ||
                          scrapingRowId != null ||
                          aiSearchingRowId != null
                        }
                        onClick={() => handleGenerateFromWebsite(row)}
                        className="px-2 py-1.5 text-[11px] rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 transition-colors inline-flex items-center gap-1 flex-shrink-0"
                      >
                        {scrapingRowId === row.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Sparkles className="w-3 h-3" />
                        )}
                        Generieren
                      </button>
                      <button
                        type="button"
                        title="Per KI im Web suchen und Kontaktfelder füllen"
                        disabled={
                          importing ||
                          bulkGenerating ||
                          bulkAiSearching ||
                          scrapingRowId != null ||
                          aiSearchingRowId != null
                        }
                        onClick={() => handleAiSearchRow(row)}
                        className="px-2 py-1.5 text-[11px] rounded-lg border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-60 transition-colors inline-flex items-center gap-1 flex-shrink-0"
                      >
                        {aiSearchingRowId === row.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Globe className="w-3 h-3" />
                        )}
                        KI-Suche
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] text-neutral-500 mb-0.5">Notizen</label>
                    <textarea
                      className="input-field !py-1.5 !text-sm resize-none"
                      rows={2}
                      value={row.contact.notes || ''}
                      disabled={importing}
                      onChange={(e) => updateRowContact(row.id, { notes: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-neutral-500 mb-1">Kategorien</label>
                    {renderCategoryChips({
                      selectedIds: row.contact.categoryIds,
                      onToggle: (catId) => toggleRowCategory(row.id, catId),
                      disabled: importing,
                      collapsible: true,
                      expanded: categoriesExpanded,
                      onToggleExpanded: () => setCategoriesExpanded((v) => !v),
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
          <button
            type="button"
            disabled={importing || bulkGenerating || bulkAiSearching || selectedCount === 0}
            onClick={importSelected}
            className="btn-primary disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {importProgress?.total
                  ? `Importiert… ${importProgress.done}/${importProgress.total}`
                  : 'Importiert…'}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                {selectedCount === 1
                  ? '1 Kontakt importieren'
                  : `${selectedCount} Kontakte importieren`}
              </>
            )}
          </button>
          <button
            type="button"
            disabled={importing || bulkGenerating || bulkAiSearching}
            onClick={onClose}
            className="btn-secondary disabled:opacity-60"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContactCsvImport;
